// src/App.js
//
// Kusa client. Three entry modes selected by URL params:
//   - no params:     Landing → Create or Join a match.
//   - ?match=ABC:    Join an existing match by code.
//   - ?seat=N:       Backward-compat dev shortcut (skip lobby, default match).
//   - ?dev=1:        4-panel local-only dev view.
import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
  createContext,
  Fragment,
  forwardRef,
} from 'react';
import { Client } from 'boardgame.io/react';
import { Local, SocketIO } from 'boardgame.io/multiplayer';
import { LobbyClient } from 'boardgame.io/client';
import {
  Kusa,
  VALID_BIDS,
  TARGET_SCORE_OPTIONS,
  legalPlayKeys,
  whyIllegal,
  trumpingPlayer,
} from './Game';
// ============================================================
// CONFIG
// ============================================================
const SERVER_URL =
  process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';
const GAME_NAME = 'kusa';
const NUM_PLAYERS = 4;
const lobbyClient = new LobbyClient({ server: SERVER_URL });
const params =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
const SEAT_PARAM = params.get('seat');
const MATCH_PARAM = params.get('match');
const DEV_PARAM = params.get('dev');
const TARGET_PARAM = Number(params.get('target')) || null;
const MODE_DEV_LOCAL = DEV_PARAM === '1';
const MODE_QUICKSEAT =
  !MODE_DEV_LOCAL && SEAT_PARAM !== null && ['0', '1', '2', '3'].includes(SEAT_PARAM);
// ============================================================
// SESSION STORAGE
//
// Persisted in localStorage (not sessionStorage) so that closing the
// tab and coming back later still rejoins the player to their seat.
// SESSION_KEY bumped to v2 so any old sessionStorage entries from
// the previous scheme don't get misinterpreted.
//
// Tradeoffs to remember:
//   - Shared computer: the last-active session auto-rejoins on next
//     visit. The MATCH_PARAM mismatch check in LobbyApp clears stale
//     sessions when the user follows a different match link.
//   - Server restart wipes in-memory match state; the rejoin will then
//     fail. PlayScreen catches that and clears the session.
//   - Finished matches: the game enters the 'complete' phase rather
//     than a terminal gameover state, so the saved session is still
//     valid — players can rejoin and either hit "Play again" or "Leave".
// ============================================================
const SESSION_KEY = 'kusa-session-v2';
const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const saveSession = (val) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(val));
  } catch {}
};
const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
};
// ============================================================
// PLAYER-NAME CONTEXT
// ============================================================
const DEFAULT_NAMES = ['Player 1', 'Player 2', 'Player 3', 'Player 4'];
const PlayerNamesContext = createContext(DEFAULT_NAMES);
const teamOf = (playerID) => Number(playerID) % 2;
const partnerOf = (playerID) => String((Number(playerID) + 2) % 4);
function usePlayerNames() {
  return useContext(PlayerNamesContext);
}
function usePlayerLabel() {
  const names = usePlayerNames();
  return useCallback(
    (playerID) =>
      playerID == null ? '' : names[Number(playerID)] ?? `P${playerID}`,
    [names]
  );
}
function useTeamLabel() {
  const names = usePlayerNames();
  return useCallback(
    (teamID) => {
      const t = Number(teamID);
      return `${names[t]}/${names[t + 2]}`;
    },
    [names]
  );
}
// ============================================================
// CARD HELPERS
// ============================================================
const COLOR_ORDER = ['red', 'yellow', 'green', 'blue'];
// Rank orderings used by the various hand-arrangement options.
// "Ben" = current default: 1 first (Ace-equivalent), then 5..14 ascending.
// "hi-lo" = trick-taking strength descending: 1, 14, 13, ..., 5.
// "lo-hi" = ascending by trick-taking strength: 5, 6, ..., 14, 1.
const RANK_ORDER_BEN = [1, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
const RANK_ORDER_HI_LO = [1, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5];
const RANK_ORDER_LO_HI = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 1];
const COUNTER_VALUE = { 1: 15, 5: 5, 10: 10, 14: 10 };
const counterOf = (c) => (c.kusa ? 20 : COUNTER_VALUE[c.rank] || 0);
const tallyCards = (cards) =>
  (cards || []).reduce((sum, c) => sum + counterOf(c), 0);
const cardKeyOf = (c) => (c.kusa ? 'KUSA' : `${c.color}-${c.rank}`);
const fileNameOf = (c) => (c.kusa ? 'kusa' : `${c.color}-${c.rank}`);
const TRUMP_STYLE = {
  red: { background: '#e74c3c', color: '#fff' },
  yellow: { background: '#f1c40f', color: '#222' },
  green: { background: '#27ae60', color: '#fff' },
  blue: { background: '#2c7be5', color: '#fff' },
  'no-trump': { background: '#444', color: '#fff' },
};
// Translucent ripple colors keyed to the trump choice. Slightly muted
// versions of the TRUMP_STYLE backgrounds so the ripple reads as a
// "wash" of that color across the table rather than a bright disc.
const TRUMP_RIPPLE_COLOR = {
  red: 'rgba(231, 76, 60, 0.55)',
  yellow: 'rgba(241, 196, 15, 0.55)',
  green: 'rgba(39, 174, 96, 0.55)',
  blue: 'rgba(44, 123, 229, 0.55)',
  'no-trump': 'rgba(200, 200, 200, 0.45)',
};
// ---- Card-arrangement preferences (per device, via localStorage) ----
const ARRANGEMENT_OPTIONS = [
  { value: 'ben', label: 'Color · 1 then 5–14' },
  { value: 'color-hi-lo', label: 'Color · high to low' },
  { value: 'color-lo-hi', label: 'Color · low to high' },
  { value: 'all-hi-lo', label: 'All cards · high to low' },
  { value: 'all-lo-hi', label: 'All cards · low to high' },
];
const TRUMP_SIDE_OPTIONS = [
  { value: 'natural', label: 'Trump: natural' },
  { value: 'left', label: 'Trump: left' },
  { value: 'right', label: 'Trump: right' },
];
const PREF_ARRANGEMENT_KEY = 'kusa.handArrangement';
const PREF_TRUMP_SIDE_KEY = 'kusa.handTrumpSide';
const PREF_SOUND_MODE_KEY = 'kusa.soundMode';

// ============================================================
// SOUND EFFECTS
//
// Audio files live in public/sounds/. We preload at module scope so
// the first play has minimal latency. Each playSound call clones the
// Audio node so overlapping triggers (e.g. flip + kusa simultaneously)
// don't cut each other off.
//
// Mode (persisted via localStorage):
//   'all'        — every event plays its sound
//   'turn-only'  — only yourturn.wav plays, everything else muted
//   'none'       — silent
// ============================================================
const SOUND_FILES = {
  yourturn: '/sounds/yourturn.wav',
  flip: '/sounds/flip.wav',
  shuffle: '/sounds/shuffle.wav',
  wingame: '/sounds/wingame.wav',
  losegame: '/sounds/losegame.wav',
  set: '/sounds/set.wav',
  trick: '/sounds/trick.wav',
  kusa: '/sounds/kusa.wav',
  moon: '/sounds/moon.wav',
  trump: '/sounds/trump.wav',
};
const SOUND_MODE_OPTIONS = [
  { value: 'all', label: 'All sounds' },
  { value: 'turn-only', label: 'My turn only' },
  { value: 'none', label: 'No sounds' },
];
const audioCache = {};
if (typeof window !== 'undefined' && typeof Audio !== 'undefined') {
  for (const [name, path] of Object.entries(SOUND_FILES)) {
    try {
      const a = new Audio(path);
      a.preload = 'auto';
      audioCache[name] = a;
    } catch {
      // If Audio() throws for some reason (e.g. exotic browser), the
      // playSound calls below silently no-op since cache entry is missing.
    }
  }
}
function playSoundWithMode(name, mode) {
  if (mode === 'none') return;
  if (mode === 'turn-only' && name !== 'yourturn') return;
  const base = audioCache[name];
  if (!base) return;
  try {
    const clone = base.cloneNode();
    clone.volume = 0.75;
    const p = clone.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    // Browsers may throw if no prior user gesture; ignore.
  }
}
const isColorArrangement = (a) =>
  a === 'ben' || a === 'color-hi-lo' || a === 'color-lo-hi';
function getStoredPref(key, fallback) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}
function setStoredPref(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}
// Compute either a column layout (for color-grouped arrangements) or a flat
// sequence (for "all cards" arrangements). Trump side controls whether the
// trump column is moved to left/right of the natural color order.
function arrangeHand(hand, trump, arrangement, trumpSide) {
  const visible = (hand || []).filter((c) => !c.hidden);
  // Flat (no color grouping): sort all cards by rank, Kusa at the high end.
  if (arrangement === 'all-hi-lo' || arrangement === 'all-lo-hi') {
    const order =
      arrangement === 'all-hi-lo' ? RANK_ORDER_HI_LO : RANK_ORDER_LO_HI;
    const sorted = [...visible].sort((a, b) => {
      // Kusa is highest trump → first in hi-lo, last in lo-hi.
      if (a.kusa && !b.kusa) return arrangement === 'all-hi-lo' ? -1 : 1;
      if (!a.kusa && b.kusa) return arrangement === 'all-hi-lo' ? 1 : -1;
      if (a.kusa && b.kusa) return 0;
      const rankCmp = order.indexOf(a.rank) - order.indexOf(b.rank);
      if (rankCmp !== 0) return rankCmp;
      // Same rank: stabilize by color order.
      return COLOR_ORDER.indexOf(a.color) - COLOR_ORDER.indexOf(b.color);
    });
    return { columns: null, flat: sorted };
  }
  // Color-grouped layouts.
  let order;
  if (arrangement === 'color-hi-lo') order = RANK_ORDER_HI_LO;
  else if (arrangement === 'color-lo-hi') order = RANK_ORDER_LO_HI;
  else order = RANK_ORDER_BEN;
  const groups = { red: [], yellow: [], green: [], blue: [], kusa: [] };
  for (const c of visible) {
    if (c.kusa) groups.kusa.push(c);
    else if (groups[c.color]) groups[c.color].push(c);
  }
  for (const color of COLOR_ORDER) {
    groups[color].sort(
      (a, b) => order.indexOf(a.rank) - order.indexOf(b.rank)
    );
  }
  // Fold Kusa into trump column (always at the top of that column).
  if (trump && trump !== 'no-trump' && groups.kusa.length > 0) {
    groups[trump] = [...groups.kusa, ...groups[trump]];
    groups.kusa = [];
  }
  // Determine column order. Default = natural color order (red/yellow/green/
  // blue) plus a Kusa column if Kusa wasn't folded in. Trump side moves the
  // trump column to the leftmost or rightmost position.
  let columnKeys = [...COLOR_ORDER, 'kusa'];
  if (trump && trump !== 'no-trump' && trumpSide && trumpSide !== 'natural') {
    columnKeys = columnKeys.filter((k) => k !== trump);
    columnKeys =
      trumpSide === 'left' ? [trump, ...columnKeys] : [...columnKeys, trump];
  }
  const columns = columnKeys
    .filter((k) => groups[k].length > 0)
    .map((k) => ({ color: k, cards: groups[k] }));
  return { columns, flat: null };
}
function validBidAmounts(currentHigh) {
  if (!currentHigh) return [...VALID_BIDS];
  return VALID_BIDS.filter((b) => b > currentHigh);
}
// ============================================================
// ANIMATED NUMBER — tweens between values with an ease-out curve
// over `duration` ms. Used in StatusBubbles for the score readouts
// so hand-end score jumps roll up satisfyingly instead of teleporting.
// Falls back to instantaneous updates if prefers-reduced-motion.
// ============================================================
function AnimatedNumber({ value, duration = 700 }) {
  const [displayed, setDisplayed] = useState(value);
  const prevValueRef = useRef(value);
  useEffect(() => {
    const from = prevValueRef.current;
    const to = value;
    if (from === to) return;
    // Respect reduced-motion: jump to the new value immediately.
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplayed(to);
      prevValueRef.current = to;
      return;
    }
    const start = performance.now();
    let frame;
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / duration, 1);
      // Cubic ease-out: fast at first, settles at the end.
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayed(Math.round(from + (to - from) * eased));
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        prevValueRef.current = to;
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);
  return <>{displayed}</>;
}

// ============================================================
// CARD COMPONENT
// ============================================================
// Width clamps from 44px (mobile) to 88px (desktop); height is derived
// from a 5:7 aspect ratio. 44px is the smallest comfortable touch target.
// At 8vw the mobile floor still kicks in on a typical 375–430px phone,
// so the phone layout is unchanged; desktop gets the visual upgrade.
const CARD_WIDTH_CSS = 'clamp(44px, 8vw, 88px)';
const Card = forwardRef(function Card(
  { card, onClick, selected, disabled, illegal, className, style },
  ref
) {
  if (!card) return null;
  const baseStyle = {
    display: 'inline-block',
    width: CARD_WIDTH_CSS,
    aspectRatio: '5 / 7',
    margin: 2,
    borderRadius: 6,
    cursor: illegal
      ? 'not-allowed'
      : onClick && !disabled
      ? 'pointer'
      : 'default',
    opacity: disabled ? 0.65 : illegal ? 0.4 : 1,
    transition: 'transform 0.12s, box-shadow 0.12s, opacity 0.12s',
    boxShadow: selected
      ? '0 0 0 3px #000, 0 4px 8px rgba(0,0,0,0.25)'
      : '0 1px 3px rgba(0,0,0,0.18)',
    transform: selected ? 'translateY(-6px)' : 'none',
    overflow: 'hidden',
    boxSizing: 'border-box',
    verticalAlign: 'top',
    background: '#fff',
  };
  const onMissing = (e, label) => {
    const t = e.currentTarget;
    t.replaceWith(
      Object.assign(document.createElement('div'), {
        style: `display:inline-flex;align-items:center;justify-content:center;width:${CARD_WIDTH_CSS};aspect-ratio:5/7;margin:2px;border-radius:6px;background:#fee;color:#900;font:10px monospace;border:1px dashed #900;text-align:center;padding:2px;box-sizing:border-box;`,
        innerText: `missing\n${label}`,
      })
    );
  };
  if (card.hidden) {
    return (
      <img
        ref={ref}
        src="/cards/back.svg"
        alt="card back"
        draggable={false}
        onClick={onClick}
        onError={(e) => onMissing(e, 'back')}
        className={className}
        style={{ ...baseStyle, objectFit: 'contain', ...style }}
      />
    );
  }
  const filename = fileNameOf(card);
  return (
    <img
      ref={ref}
      src={`/cards/${filename}.svg`}
      alt={filename}
      draggable={false}
      onClick={onClick}
      onError={(e) => onMissing(e, filename)}
      className={className}
      style={{ ...baseStyle, objectFit: 'contain', ...style }}
    />
  );
});
// ============================================================
// STATUS BUBBLES (3-up: team A · team B · bid)
// ============================================================
function StatusBubbles({ G, ctx, handCounters }) {
  const teamLabel = useTeamLabel();
  const playerLabel = usePlayerLabel();
  const declarerTeam = G.declarer != null ? teamOf(G.declarer) : null;
  const contract = G.contractAmount;
  const isGameOver = ctx.phase === 'complete';
  // Round any "need to set" amount up to the next achievable multiple of 5
  // (since all counter values are multiples of 5, you can't reach an
  // odd-modulo number exactly). "Need to make" is already a multiple of 5
  // because contract and captured counters both are.
  const roundUpFive = (n) => Math.ceil(n / 5) * 5;
  // Compute the status sentence for a team. Returns null if no contract
  // is set yet (during bidding).
  function needLine(team) {
    if (declarerTeam == null || contract == null) return null;
    const myCounters = handCounters[team];
    if (team === declarerTeam) {
      if (myCounters >= contract) {
        return { text: 'Made the bid!', good: true };
      }
      const need = contract - myCounters;
      if (isGameOver) {
        return {
          text: `Needed ${need} more points to make the bid.`,
          missed: true,
        };
      }
      return { text: `Need ${need} more points to make the bid.` };
    } else {
      // Opponents need strictly more than (180 - contract) counters to set.
      const setThreshold = 181 - contract;
      if (myCounters >= setThreshold) {
        return { text: 'Set the opponents!', good: true };
      }
      const need = roundUpFive(setThreshold - myCounters);
      if (isGameOver) {
        return {
          text: `Needed ${need} more points to set opponents.`,
          missed: true,
        };
      }
      return { text: `Need ${need} more points to set opponents.` };
    }
  }
  // Dark-surface variants of the team / bid bubbles. Same warm-vs-cool
  // contrast as before (team 0 = warm, team 1 = cool) but rendered as
  // lit panels on the charcoal tabletop rather than cream/blue tints
  // on a white background.
  const teamBubble = (team) => {
    const need = needLine(team);
    const warm = team === 0;
    // Wabi-sabi palette:
    //   Team 0 (warm) = kakishibu persimmon — fermented-persimmon brown
    //   Team 1 (cool) = ai indigo — the classic Japanese natural-dye blue
    return (
      <div
        key={team}
        style={{
          padding: '8px 10px',
          background: warm ? '#6e3a1e' : '#1f3a5e',
          border: '1px solid ' + (warm ? '#a8602e' : '#3a6890'),
          borderRadius: 8,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: warm ? '#f0c8a0' : '#b8d0e8',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 4,
          }}
          title={teamLabel(team)}
        >
          {teamLabel(team)}
        </div>
        <div style={{ fontSize: 11, color: warm ? '#d8a878' : '#88a8c8' }}>
          Game score:{' '}
          <b style={{ fontSize: 14, color: '#fff' }}>
            <AnimatedNumber value={G.score[team]} />
          </b>
          <span style={{ color: warm ? '#a07a52' : '#5e7a96' }}>
            {' / '}
            {G.targetScore || 300}
          </span>
        </div>
        <div style={{ fontSize: 11, color: warm ? '#d8a878' : '#88a8c8' }}>
          This hand:{' '}
          <b style={{ fontSize: 13, color: '#fff' }}>{handCounters[team]}</b>
        </div>
        {need && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              fontWeight: need.good ? 700 : 600,
              color: need.good
                ? '#a8d8a8'
                : need.missed
                ? (warm ? '#a07a52' : '#5e7a96')
                : '#fff',
              textDecoration: need.missed ? 'line-through' : 'none',
            }}
          >
            {need.text}
          </div>
        )}
      </div>
    );
  };
  const bidHeader = (
    <div
      style={{
        fontSize: 13,
        fontWeight: 800,
        color: '#c8d0bc',
        letterSpacing: 1.5,
        marginBottom: 4,
      }}
    >
      BID
    </div>
  );
  const bidBubbleStyle = {
    padding: '8px 10px',
    // Bid bubble = rikyu-nezumi (tea-master green-gray). Named for
    // Sen no Rikyū, the 16th-century tea master — a sage-gray that
    // sits between the persimmon and indigo without competing.
    background: '#3a4838',
    border: '1px solid #6a7560',
    borderRadius: 8,
    minWidth: 0,
  };
  let bidBubble;
  if (G.declarer == null) {
    bidBubble = (
      <div style={bidBubbleStyle}>
        {bidHeader}
        <div style={{ color: '#8a9080', fontStyle: 'italic', fontSize: 12 }}>
          Awaiting bid
        </div>
      </div>
    );
  } else {
    const trumpCallerNote =
      G.trumpCaller && G.trumpCaller !== G.declarer
        ? ` (called by ${playerLabel(G.trumpCaller)})`
        : '';
    bidBubble = (
      <div style={bidBubbleStyle}>
        {bidHeader}
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#fff',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 2,
          }}
          title={teamLabel(declarerTeam)}
        >
          {teamLabel(declarerTeam)}
        </div>
        <div style={{ fontSize: 11, color: '#a8b09c' }}>
          Target:{' '}
          <b style={{ fontSize: 13, color: '#fff' }}>{G.contractAmount}</b>
        </div>
        <div style={{ fontSize: 11, color: '#a8b09c', marginTop: 2 }}>
          Trump:{' '}
          {G.trump ? (
            <span
              style={{
                ...(TRUMP_STYLE[G.trump] || {}),
                padding: '1px 6px',
                borderRadius: 3,
                fontWeight: 700,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {G.trump === 'no-trump' ? 'No Trump' : G.trump}
            </span>
          ) : (
            <em style={{ color: '#8a9080' }}>calling…</em>
          )}
          {trumpCallerNote && (
            <span style={{ color: '#9aa090', fontSize: 11 }}>
              {trumpCallerNote}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
        gap: 8,
        marginBottom: 10,
      }}
    >
      {teamBubble(0)}
      {teamBubble(1)}
      {bidBubble}
    </div>
  );
}
// ============================================================
// BID SEQUENCE (4-column live auction grid, shown during bidding)
// ============================================================
function BidSequence({ G, ctx }) {
  const playerLabel = usePlayerLabel();
  // Snapshot how many bidHistory entries existed on first render so we
  // can animate only NEW additions. The ref starts null and gets
  // populated in a useEffect after the first non-empty render.
  const initialBidCountRef = useRef(null);
  useEffect(() => {
    if (
      initialBidCountRef.current === null &&
      G.bidHistory &&
      G.bidHistory.length > 0
    ) {
      initialBidCountRef.current = G.bidHistory.length;
    }
    // Reset the snapshot if the bid history is cleared (new hand) so
    // the next round's first bid gets the animation as expected.
    if (G.bidHistory && G.bidHistory.length === 0) {
      initialBidCountRef.current = null;
    }
  }, [G.bidHistory]);
  if (ctx.phase !== 'bidding') return null;
  if (!G.bidHistory || G.bidHistory.length === 0) return null;
  // Column order: left of dealer first, then clockwise around the table.
  const colOrder = [0, 1, 2, 3].map((i) => String((G.dealer + 1 + i) % 4));
  // Group bidHistory into rows. A player's row index = how many actions
  // they have already taken when this entry was logged. Stash the
  // bidHistory index on each entry so renderCell can decide whether
  // this cell is a "new" arrival worth animating.
  const counts = { 0: 0, 1: 0, 2: 0, 3: 0 };
  const passedAtRow = {};
  const rows = [];
  G.bidHistory.forEach((entry, historyIdx) => {
    const row = counts[entry.player];
    while (rows.length <= row) rows.push({});
    rows[row][entry.player] = { ...entry, historyIdx };
    if (entry.action === 'pass') passedAtRow[entry.player] = row;
    counts[entry.player]++;
  });
  const isNewEntry = (entry) =>
    initialBidCountRef.current !== null &&
    entry.historyIdx >= initialBidCountRef.current;
  const renderCell = (rowIdx, playerID) => {
    const a = rows[rowIdx][playerID];
    if (a) {
      const newClass = isNewEntry(a) ? ' kusa-bid-cell-new' : '';
      if (a.action === 'bid') {
        return (
          <b
            className={`${newClass}`}
            style={{ color: '#f0f0f4' }}
          >
            {a.amount}
          </b>
        );
      }
      if (a.action === 'pass') {
        return (
          <span
            className={`${newClass}`}
            style={{ color: '#8a909a' }}
          >
            pass
          </span>
        );
      }
      if (a.action === 'hold') {
        return (
          <em
            className={`${newClass}`}
            style={{ color: '#bcc0c8', fontStyle: 'italic' }}
          >
            hold
          </em>
        );
      }
    }
    if (passedAtRow[playerID] != null && passedAtRow[playerID] < rowIdx) {
      return <span style={{ color: '#5b606a' }}>—</span>;
    }
    return null;
  };
  return (
    <div
      style={{
        marginBottom: 10,
        padding: 8,
        background: '#2a2f3a',
        border: '1px solid #444b59',
        borderRadius: 6,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: '#e1e3ea',
          letterSpacing: 1,
          marginBottom: 4,
        }}
      >
        BIDDING
      </div>
      <table
        style={{
          width: '100%',
          fontSize: 12,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            {colOrder.map((p) => (
              <th
                key={p}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 4px',
                  textAlign: 'center',
                  color: '#aab0b8',
                  borderBottom: '1px solid #444b59',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={playerLabel(p)}
              >
                {playerLabel(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((_, rowIdx) => (
            <tr key={rowIdx}>
              {colOrder.map((p) => (
                <td
                  key={p}
                  style={{
                    padding: '3px 4px',
                    textAlign: 'center',
                    fontSize: 12,
                  }}
                >
                  {renderCell(rowIdx, p)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
// ============================================================
// POSITIONAL TRICK (3x3 grid: cards land in their player's seat
// relative to the viewer — bottom is you, top is partner, left/right
// are opponents)
// ============================================================
// Grid positions of the four seats inside PositionalTrick. Used by the
// trick-sweep math to compute how far each non-winner card should
// translate toward the winner's seat.
const POSITION_COORDS = {
  top:    { col: 1, row: 0 },
  bottom: { col: 1, row: 2 },
  left:   { col: 0, row: 1 },
  right:  { col: 2, row: 1 },
};
function sweepStyleFor(myPos, winnerPos) {
  if (!myPos || !winnerPos || myPos === winnerPos) return null;
  const me = POSITION_COORDS[myPos];
  const win = POSITION_COORDS[winnerPos];
  // Each grid step is roughly one card width (col) or one card height
  // (row). Multiplying by 80px gives a "card lands on top of winner's"
  // visual at typical desktop card sizes; scales acceptably on mobile.
  const dx = (win.col - me.col) * 80;
  const dy = (win.row - me.row) * 100;
  return {
    '--sweep-x': `${dx}px`,
    '--sweep-y': `${dy}px`,
  };
}

function PositionalTrick({
  trick,
  viewerSeat,
  trumpingSeat,
  winningSeat,
  activeSeat,
  isSweeping,
  playFlight,
  onFlightComplete,
}) {
  const playerLabel = usePlayerLabel();
  // Index 0 = bottom (me), 1 = left (next in turn order, sits to my left
  // because turn rotation is clockwise around the table), 2 = top (partner
  // across), 3 = right (previous in turn order — they just played before me).
  const positions = ['bottom', 'left', 'top', 'right'];
  // Build a slot record per position with the seat ID that occupies it
  // (independent of whether they've played a card yet) and the played
  // card if any. We need the seat ID for the always-visible name plate.
  const slots = {};
  for (let i = 0; i < 4; i++) {
    const seatID = String((Number(viewerSeat) + i) % 4);
    slots[positions[i]] = { seatID, entry: null };
  }
  for (const entry of trick) {
    const rel = (Number(entry.player) - Number(viewerSeat) + 4) % 4;
    slots[positions[rel]].entry = entry;
  }
  // Find the winner's position (relative to viewer) for the sweep math.
  const winningPos =
    isSweeping && winningSeat
      ? positions.find((p) => slots[p].seatID === winningSeat)
      : null;
  // Slot refs — one per position. Used by the FLIP card-flight effect
  // to measure the destination rect after the card lands in the slot.
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const slotRefs = useMemo(
    () => ({
      top: topRef,
      bottom: bottomRef,
      left: leftRef,
      right: rightRef,
    }),
    []
  );
  // Tracks the most recent playFlight stamp we've already animated so we
  // don't double-fire when the effect re-runs (e.g. trick prop updates
  // in a render after playFlight was set).
  const processedFlightStampRef = useRef(null);
  // FLIP card-flight effect. Runs on both playFlight changes AND trick
  // changes — because boardgame.io's state update for the played card
  // may arrive in a different render than our local setPlayFlight, so
  // we need to retry once the slot actually contains the card.
  useLayoutEffect(() => {
    if (!playFlight) return;
    if (processedFlightStampRef.current === playFlight.stamp) return;
    // Find which position holds the flying card.
    let targetPos = null;
    for (const pos of positions) {
      const entry = slots[pos].entry;
      if (entry && cardKeyOf(entry.card) === playFlight.cardKey) {
        targetPos = pos;
        break;
      }
    }
    if (!targetPos) return; // card not in slot yet — wait for the next render
    const cardEl = slotRefs[targetPos].current;
    if (!cardEl) return;
    // Mark this flight as processed BEFORE starting the animation so
    // any cascading re-render doesn't double-trigger it.
    processedFlightStampRef.current = playFlight.stamp;
    const destRect = cardEl.getBoundingClientRect();
    const dx = playFlight.fromRect.left - destRect.left;
    const dy = playFlight.fromRect.top - destRect.top;
    const scale = destRect.width > 0
      ? playFlight.fromRect.width / destRect.width
      : 1;
    const anim = cardEl.animate(
      [
        {
          transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
          opacity: 1,
        },
        {
          transform: 'translate(0, 0) scale(1)',
          opacity: 1,
        },
      ],
      {
        duration: 420,
        easing: 'cubic-bezier(0.34, 0.0, 0.2, 1)',
        fill: 'both',
      }
    );
    anim.onfinish = () => {
      // Detach the "fill: both" sticky state so the element renders at
      // its natural transform afterward.
      anim.cancel();
      if (onFlightComplete) onFlightComplete();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playFlight, trick]);

  const renderSlot = (pos) => {
    const { seatID, entry } = slots[pos];
    const team = teamOf(seatID);
    const warm = team === 0;
    const isMine = String(seatID) === String(viewerSeat);
    const isTrumping = trumpingSeat === seatID;
    // The winning seat highlight (green pulse) is only applied during
    // the lingering phase — once the trick has resolved. The trump-burn
    // (amber) wins out visually if both apply to the same seat.
    const isWinning = winningSeat === seatID;
    // The active-turn glow (cyan pulse) is applied to whichever seat
    // needs to play next during the live trick. Priority below the
    // other two so it never overrides "winning by trumping in" or
    // "just took the trick."
    const isActive = activeSeat === seatID;
    const seatClass = isTrumping
      ? 'kusa-seat kusa-seat-trumping'
      : isWinning
      ? 'kusa-seat kusa-seat-trick-won'
      : isActive
      ? 'kusa-seat kusa-seat-active'
      : 'kusa-seat';
    // During the sweep phase: non-winner cards translate toward the
    // winner's slot and fade out; the winner's own card does a brief
    // scale-up bounce to register "this is where the trick goes."
    const cardSweepClass =
      isSweeping && entry
        ? isWinning
          ? 'kusa-card-sweep-winner'
          : 'kusa-card-sweep'
        : 'kusa-card-land';
    const cardSweepStyle =
      isSweeping && entry && !isWinning && winningPos
        ? sweepStyleFor(pos, winningPos)
        : null;
    // When a card is currently flighting via FLIP (the local player
    // just played it), suppress the kusa-card-land entry animation so
    // the WAAPI flight handles all the motion uncontested.
    const isFlighting =
      playFlight && entry && cardKeyOf(entry.card) === playFlight.cardKey;
    const finalCardClass = isFlighting ? '' : cardSweepClass;
    return (
      <div
        className={seatClass}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          padding: 6,
          borderRadius: 8,
          // Subtle team-tinted outline so partnerships read at a glance.
          // Warm persimmon for team 0, cool indigo for team 1. The
          // trumping override comes from .kusa-seat-trumping CSS.
          border: '1px solid ' + (warm ? 'rgba(168,96,46,0.45)' : 'rgba(58,104,144,0.45)'),
          background: 'rgba(255,255,255,0.02)',
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: warm ? '#d8a878' : '#88a8c8',
            maxWidth: 88,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: 0.3,
          }}
          title={playerLabel(seatID)}
        >
          {playerLabel(seatID)}
          {isMine && ' (you)'}
        </div>
        {entry ? (
          <Card
            ref={slotRefs[pos]}
            card={entry.card}
            disabled
            className={finalCardClass}
            style={cardSweepStyle || undefined}
          />
        ) : (
          // Empty placeholder at the same dimensions as a card, so the
          // slot doesn't reflow when a card lands. Faint dashed outline
          // hints at "card goes here."
          <div
            style={{
              width: CARD_WIDTH_CSS,
              aspectRatio: '5 / 7',
              margin: 2,
              borderRadius: 6,
              border: '1px dashed rgba(255,255,255,0.10)',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
    );
  };
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateAreas:
          '". top ." "left . right" ". bottom ."',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto auto auto',
        gap: 6,
        // Was fixed at 320; now clamps so desktop gets a more
        // table-like central play area while mobile is unchanged.
        maxWidth: 'clamp(320px, 50vw, 520px)',
        margin: '8px auto',
        placeItems: 'center',
        minHeight: 'clamp(180px, 40vw, 320px)',
      }}
    >
      <div style={{ gridArea: 'top' }}>{renderSlot('top')}</div>
      <div style={{ gridArea: 'left' }}>{renderSlot('left')}</div>
      <div style={{ gridArea: 'right' }}>{renderSlot('right')}</div>
      <div style={{ gridArea: 'bottom' }}>{renderSlot('bottom')}</div>
    </div>
  );
}
// ============================================================
// CHAT (server-persisted via custom REST endpoint at /chat/<matchID>)
// All players in the match see the full message history — including
// messages sent before they joined. Polls every 2s for new messages.
// ============================================================
function Chat({ matchID, mySeat }) {
  const playerLabel = usePlayerLabel();
  const [inputText, setInputText] = useState('');
  // Open by default so chat is visible and inviting. Toggle still works.
  const [isOpen, setIsOpen] = useState(true);
  const [lastSeenCount, setLastSeenCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  // Read joinedAt from localStorage so we can render a "before you
  // joined" divider for any messages older than the join timestamp.
  // Read once at mount — it doesn't change after.
  const joinedAtRef = useRef(null);
  // Tracks the message-count threshold below which entries should NOT
  // play the entry animation. Set after the first render that receives
  // any messages, so historical messages (the initial fetch) appear
  // statically and only subsequent additions slide in.
  const initialMessageCountRef = useRef(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.joinedAt === 'number') {
          joinedAtRef.current = parsed.joinedAt;
        }
      }
    } catch {
      // Silent fail; divider just won't appear.
    }
  }, []);
  // Initial fetch + polling for new messages.
  useEffect(() => {
    if (!matchID) return;
    let live = true;
    const fetchMessages = async () => {
      try {
        const res = await fetch(
          `${SERVER_URL}/chat/${encodeURIComponent(matchID)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (live && Array.isArray(data.messages)) {
          setMessages(data.messages);
        }
      } catch {
        // Silent fail; will retry on next interval.
      }
    };
    fetchMessages();
    const id = setInterval(fetchMessages, 2000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [matchID]);
  // Auto-scroll to bottom when a new message arrives, only while open.
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isOpen]);
  // When chat is opened, mark all current messages as read.
  useEffect(() => {
    if (isOpen) setLastSeenCount(messages.length);
  }, [isOpen, messages.length]);
  // First time we receive any messages, snapshot the count. Future
  // additions (index >= this count) get the entry animation.
  useEffect(() => {
    if (initialMessageCountRef.current === null && messages.length > 0) {
      initialMessageCountRef.current = messages.length;
    }
  }, [messages.length]);
  const unread = isOpen ? 0 : Math.max(0, messages.length - lastSeenCount);
  // Shared sender used by both the free-text form submit and the
  // quick-reaction buttons. Returns the optimistic message so callers
  // can chain (e.g. clear the input field).
  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || !matchID) return;
    try {
      const res = await fetch(
        `${SERVER_URL}/chat/${encodeURIComponent(matchID)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sender: mySeat, text: trimmed }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.message) {
          setMessages((prev) => {
            // Avoid duplicating if a poll already grabbed it.
            if (prev.some((x) => x.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        }
      }
    } catch {
      // Silent fail.
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = inputText.trim();
    if (!trimmed) return;
    setInputText('');
    await sendMessage(trimmed);
  };
  const sendReaction = (text) => {
    if (!isOpen) setIsOpen(true);
    sendMessage(text);
  };
  // Preset emoji quick-reactions. Each is shown as a chip above the
  // chat input; clicking one sends it as a normal chat message. The
  // emoji + word combo is more readable than emoji alone, and the
  // emoji helps it scan at a glance.
  const QUICK_REACTIONS = [
    '👍 nice',
    '🎉 yes!',
    '🤔 hmm',
    '😬 oof',
    '❓ what?',
    '👋 hi',
  ];
  if (!matchID) return null;

  // Decide where to insert the "Before you joined" divider — between
  // the last message older than joinedAt and the first message newer.
  // If joinedAt is missing or all/no messages are older, no divider.
  const joinedAt = joinedAtRef.current;
  let dividerIndex = -1;
  if (joinedAt && messages.length > 0) {
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].timestamp >= joinedAt) {
        dividerIndex = i;
        break;
      }
    }
    // No new messages since join → no divider needed.
    if (dividerIndex === 0) dividerIndex = -1;
  }
  // Animate only messages added after our initial-render snapshot. If
  // the ref is still null, we haven't seen messages yet — once it's
  // set below in useEffect, any index >= it is a "new" arrival.
  const animateMessage = (i) =>
    initialMessageCountRef.current !== null &&
    i >= initialMessageCountRef.current;

  return (
    <div className="kusa-sidebar-section kusa-panel">
      <div
        className="kusa-chat-header"
        onClick={() => setIsOpen((o) => !o)}
      >
        <b>
          Chat
          {messages.length > 0 && (
            <span style={{ opacity: 0.55, fontWeight: 400, marginLeft: 6 }}>
              {messages.length}
            </span>
          )}
        </b>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {unread > 0 && (
            <span className="kusa-chat-unread-badge">{unread} new</span>
          )}
          <span className="kusa-chat-toggle">{isOpen ? '−' : '+'}</span>
        </span>
      </div>
      {isOpen && (
        <>
          <div className="kusa-chat-messages">
            {messages.length === 0 ? (
              <div className="kusa-chat-empty">
                Quiet here. Say hi to everyone.
              </div>
            ) : (
              messages.map((m, i) => {
                const isMine = String(m.sender) === String(mySeat);
                const senderTeam = teamOf(m.sender);
                const tintClass =
                  senderTeam === 0
                    ? 'kusa-chat-bubble-warm'
                    : 'kusa-chat-bubble-cool';
                const enterClass = animateMessage(i)
                  ? ' kusa-chat-bubble-enter'
                  : '';
                const bubbleClass = isMine
                  ? `kusa-chat-bubble ${tintClass} kusa-chat-bubble-own${enterClass}`
                  : `kusa-chat-bubble ${tintClass}${enterClass}`;
                return (
                  <Fragment key={m.id}>
                    {dividerIndex === i && (
                      <div className="kusa-chat-divider">
                        From before you joined ends here
                      </div>
                    )}
                    <div className={bubbleClass}>
                      {!isMine && (
                        <div className="kusa-chat-sender">
                          {playerLabel(m.sender)}
                        </div>
                      )}
                      <div>{m.text}</div>
                    </div>
                  </Fragment>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="kusa-chat-reactions">
            {QUICK_REACTIONS.map((reaction) => (
              <button
                key={reaction}
                type="button"
                onClick={() => sendReaction(reaction)}
                title={`Send "${reaction}"`}
              >
                {reaction}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="kusa-chat-input-row">
            <input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type a message…"
              maxLength={200}
            />
            <button type="submit" disabled={!inputText.trim()}>
              Send
            </button>
          </form>
        </>
      )}
    </div>
  );
}
// ============================================================
// BOARD
// ============================================================
// ============================================================
// WAITING ROOM BOARD (rendered inside the Client until all four
// players have joined; chat works here too)
// ============================================================
function WaitingRoomBoard({ matchData, mySeat, matchID }) {
  const seats = matchData || [];
  const filled = seats.filter((s) => s.name).length;
  const myEntry = seats.find((s) => String(s.id) === String(mySeat));
  const myName = myEntry?.name || '(you)';
  return (
    <div
      className="kusa-game-layout"
      style={{ fontSize: 13, fontFamily: 'system-ui, sans-serif' }}
    >
      <div className="kusa-main kusa-tabletop-rim">
        <div className="kusa-tabletop-surface">
          <div
            style={{
              fontSize: 14,
              marginBottom: 14,
              color: '#e1e3ea',
              textAlign: 'center',
            }}
          >
            You're in as <b style={{ color: '#fff' }}>{myName}</b> in seat{' '}
            <b style={{ color: '#fff' }}>{Number(mySeat) + 1}</b>.
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#aab0b8',
                fontStyle: 'italic',
              }}
            >
              {filled === NUM_PLAYERS
                ? 'All four seats filled. Starting…'
                : `Waiting for ${NUM_PLAYERS - filled} more ${
                    NUM_PLAYERS - filled === 1 ? 'player' : 'players'
                  }.`}
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 14,
            }}
          >
            {seats.map((s) => {
              const team = teamOf(s.id);
              const warm = team === 0;
              const isMine = String(s.id) === String(mySeat);
              return (
                <div
                  key={s.id}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    // Dark variants of the team-bubble palette — these
                    // are panels sitting on the charcoal surface, so
                    // they need the same persimmon/indigo treatment as
                    // the in-game StatusBubbles.
                    background: warm ? '#6e3a1e' : '#1f3a5e',
                    border:
                      '1px solid ' + (warm ? '#a8602e' : '#3a6890'),
                    opacity: s.name ? 1 : 0.6,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: warm ? '#f0c8a0' : '#b8d0e8',
                    }}
                  >
                    Seat {Number(s.id) + 1}
                    {isMine && (
                      <span
                        style={{
                          color: warm ? '#d8a878' : '#88a8c8',
                          fontWeight: 500,
                          fontSize: 11,
                          marginLeft: 4,
                        }}
                      >
                        (you)
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: warm ? '#d8a878' : '#88a8c8',
                    }}
                  >
                    Team {team + 1}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      color: '#fff',
                      fontWeight: s.name ? 500 : 400,
                    }}
                  >
                    {s.name || (
                      <em
                        style={{
                          color: warm ? '#a07a52' : '#5e7a96',
                          fontWeight: 400,
                        }}
                      >
                        waiting…
                      </em>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div
            style={{
              padding: 12,
              background: 'rgba(184, 154, 106, 0.12)',
              border: '1px solid rgba(184, 154, 106, 0.4)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: '#d4be78',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Share this with the others
            </div>
            <code
              style={{
                display: 'block',
                padding: '8px 10px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(184, 154, 106, 0.35)',
                borderRadius: 4,
                fontSize: 14,
                color: '#f5dca8',
                userSelect: 'all',
                wordBreak: 'break-all',
              }}
            >
              {window.location.origin}/?match={matchID}
            </code>
            <div
              style={{
                marginTop: 6,
                color: '#aab0b8',
                fontSize: 12,
              }}
            >
              Or just the code:{' '}
              <b
                style={{
                  color: '#f5dca8',
                  fontSize: 14,
                  letterSpacing: 1,
                }}
              >
                {matchID}
              </b>
            </div>
          </div>
        </div>
      </div>
      <div className="kusa-sidebar">
        <Chat matchID={matchID} mySeat={mySeat} />
        <RulesPanel />
      </div>
    </div>
  );
}
function KusaBoard({
  G,
  ctx,
  moves,
  playerID,
  matchData,
  matchID,
}) {
  // Derive player names from boardgame.io's matchData (live via WebSocket).
  // Fall back to "Player N" for any seat that hasn't joined yet.
  const names = useMemo(() => {
    const out = [...DEFAULT_NAMES];
    if (matchData) {
      for (const p of matchData) {
        if (p.name) out[p.id] = p.name;
      }
    }
    return out;
  }, [matchData]);
  // If not all seats have joined yet, show the lobby waiting room (with
  // chat enabled). Once everyone's in, switch to the real game UI.
  const allJoined =
    matchData &&
    matchData.length === NUM_PLAYERS &&
    matchData.every((s) => s.name);
  if (!allJoined) {
    return (
      <PlayerNamesContext.Provider value={names}>
        <WaitingRoomBoard
          matchData={matchData}
          mySeat={playerID}
          matchID={matchID}
        />
      </PlayerNamesContext.Provider>
    );
  }
  return (
    <PlayerNamesContext.Provider value={names}>
      <KusaGameBoard
        G={G}
        ctx={ctx}
        moves={moves}
        playerID={playerID}
        matchID={matchID}
      />
    </PlayerNamesContext.Provider>
  );
}
// ============================================================
// KUSA GAME BOARD (the in-progress game UI; rendered once all
// four players have joined the match)
// ============================================================
function KusaGameBoard({ G, ctx, moves, playerID, matchID }) {
  const playerLabel = usePlayerLabel();
  const teamLabel = useTeamLabel();
  const myTurn = ctx.currentPlayer === playerID;
  const hand = G.hands[playerID] || [];
  const hasPassed =
    ctx.phase === 'bidding' && G.passes.includes(playerID);
  const [bidAmount, setBidAmount] = useState(VALID_BIDS[0]);
  const [picked, setPicked] = useState([]);
  const [trumpChoice, setTrumpChoice] = useState('red');
  const [errorMsg, setErrorMsg] = useState(null);
  // Lingering trick: when a trick just resolved, hold the 4-card display
  // visible so players can see the last card. Then sweep the non-winner
  // cards toward the winner's seat before clearing.
  const [lingerTrick, setLingerTrick] = useState(null);
  const [isSweeping, setIsSweeping] = useState(false);
  // Trump-declaration ripple: when G.trump goes from null → a real value,
  // we render a temporary colored ripple effect inside the tabletop. The
  // state holds the ripple's color + a unique key so React replays the
  // animation on each new declaration (e.g. after Play Again).
  const [trumpRipple, setTrumpRipple] = useState(null);
  // FLIP card-flight state: when the local player plays a card, we
  // capture its on-screen rect at click time and pass it down to
  // PositionalTrick, which animates the matching slot card from this
  // source rect to its real position. Cleared when the animation ends.
  const [playFlight, setPlayFlight] = useState(null);
  // Hand-arrangement preferences (persisted in localStorage).
  const [arrangement, setArrangement] = useState(() =>
    getStoredPref(PREF_ARRANGEMENT_KEY, 'ben')
  );
  const [trumpSide, setTrumpSide] = useState(() =>
    getStoredPref(PREF_TRUMP_SIDE_KEY, 'natural')
  );
  // Sound preference (persisted in localStorage). Controls whether
  // sound-effect events actually emit audio. Wired up in trigger
  // effects further down via the playSound callback.
  const [soundMode, setSoundMode] = useState(() =>
    getStoredPref(PREF_SOUND_MODE_KEY, 'all')
  );
  const playSound = useCallback(
    (name) => playSoundWithMode(name, soundMode),
    [soundMode]
  );
  useEffect(() => {
    if (!errorMsg) return;
    const t = setTimeout(() => setErrorMsg(null), 2500);
    return () => clearTimeout(t);
  }, [errorMsg]);
  // Turn indicator: change browser tab title when it's your turn.
  // Other tabs (different seats) get their own document.title independently.
  useEffect(() => {
    if (myTurn) {
      const prev = document.title;
      document.title = '◉ Your turn — Kusa';
      return () => {
        document.title = prev;
      };
    }
  }, [myTurn]);
  // Lingering-trick effect: trickCount > 0 + currentTrick empty means a
  // trick just resolved. Show G.lastTrick for 3s with the green winner
  // glow, then sweep for 600ms (non-winner cards translate toward the
  // winner), then clear. If the next player plays during this window,
  // the auto-cancel below clears the whole thing early.
  useEffect(() => {
    if (G.trickCount > 0 && G.lastTrick && G.currentTrick.length === 0) {
      setLingerTrick(G.lastTrick);
      setIsSweeping(false);
      const sweepStart = setTimeout(() => {
        setIsSweeping(true);
      }, 3000);
      const clearAll = setTimeout(() => {
        setLingerTrick(null);
        setIsSweeping(false);
      }, 3600);
      return () => {
        clearTimeout(sweepStart);
        clearTimeout(clearAll);
      };
    } else if (G.currentTrick.length > 0) {
      setLingerTrick(null);
      setIsSweeping(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [G.trickCount, G.currentTrick.length]);
  // Detect trump-declaration to trigger the ripple AND trump.wav. The
  // ref tracks the previous G.trump so we fire only on null→value
  // transitions, not on every render where trump is set (e.g. opening
  // the tab while a hand is already in play). Plays for everyone.
  const prevTrumpRef = useRef(G.trump);
  useEffect(() => {
    const prev = prevTrumpRef.current;
    prevTrumpRef.current = G.trump;
    if (G.trump && !prev) {
      setTrumpRipple({ color: G.trump, key: Date.now() });
      playSound('trump');
      const t = setTimeout(() => setTrumpRipple(null), 1300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [G.trump]);

  // ============================================================
  // SOUND EFFECT TRIGGERS
  //
  // Each effect detects a state transition and calls playSound. The
  // playSound helper itself respects the soundMode preference, so
  // these all "fire" unconditionally and the mute happens downstream.
  //
  // Refs are used liberally to capture previous values so we can
  // detect transitions without firing on initial mount or on render
  // cycles where the relevant state hasn't actually changed.
  // ============================================================

  // YOUR TURN — when myTurn transitions to true. Only the local seat.
  const prevMyTurnRef = useRef(myTurn);
  useEffect(() => {
    const prev = prevMyTurnRef.current;
    prevMyTurnRef.current = myTurn;
    if (myTurn && !prev) playSound('yourturn');
  }, [myTurn, playSound]);

  // FLIP + KUSA-IN-TRICK — when a new card lands in the trick area.
  // Plays flip.wav always; plays kusa.wav too if the newly-landed
  // card is the Kusa.
  const prevTrickLenRef = useRef(0);
  useEffect(() => {
    const prev = prevTrickLenRef.current;
    const now = G.currentTrick.length;
    prevTrickLenRef.current = now;
    if (now > prev && now > 0) {
      const lastCard = G.currentTrick[now - 1].card;
      playSound('flip');
      if (lastCard && lastCard.kusa) playSound('kusa');
    }
  }, [G.currentTrick.length, G.currentTrick, playSound]);

  // Extracted so the dependency arrays below can use a simple variable
  // (the lint rule react-hooks/exhaustive-deps disallows complex
  // expressions inline in deps arrays).
  const handHistoryLen = (G.handHistory && G.handHistory.length) || 0;

  // SHUFFLE — fires when handHistory grows OR resets, indicating a
  // new deal. Skips initial mount (prev === null) so reconnects don't
  // replay the shuffle. Also fires after Play Again (history is reset
  // to [] then game starts fresh).
  const prevHandHistoryLenRef = useRef(null);
  useEffect(() => {
    const prev = prevHandHistoryLenRef.current;
    if (prev !== null && prev !== handHistoryLen) {
      playSound('shuffle');
    }
    prevHandHistoryLenRef.current = handHistoryLen;
  }, [handHistoryLen, playSound]);

  // SET + MOON — fires when handHistory grows AND the newest entry
  // has the relevant property. Both play for everybody.
  const prevHandsForResultRef = useRef(null);
  useEffect(() => {
    const prev = prevHandsForResultRef.current;
    if (prev !== null && handHistoryLen > prev) {
      const lastHand = G.handHistory[handHistoryLen - 1];
      if (lastHand && !lastHand.made) playSound('set');
      if (lastHand && lastHand.hungTheMoon != null) playSound('moon');
    }
    prevHandsForResultRef.current = handHistoryLen;
  }, [handHistoryLen, G.handHistory, playSound]);

  // TRICK WON — fires when trickCount increases AND lastTrickWinner
  // is on my team. Plays only for the winning partnership.
  const prevTrickCountRef = useRef(G.trickCount || 0);
  useEffect(() => {
    const prev = prevTrickCountRef.current;
    const now = G.trickCount || 0;
    prevTrickCountRef.current = now;
    if (now > prev && G.lastTrickWinner != null) {
      if (teamOf(G.lastTrickWinner) === teamOf(playerID)) {
        playSound('trick');
      }
    }
  }, [G.trickCount, G.lastTrickWinner, playerID, playSound]);

  // WIN GAME / LOSE GAME — fires when ctx.phase transitions to
  // 'complete'. Each player hears the version appropriate to their team.
  const prevPhaseRef = useRef(ctx.phase);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = ctx.phase;
    if (prev !== 'complete' && ctx.phase === 'complete') {
      const myTeam = teamOf(playerID);
      const winningTeam = G.score[0] > G.score[1] ? 0 : 1;
      if (myTeam === winningTeam) playSound('wingame');
      else playSound('losegame');
    }
  }, [ctx.phase, G.score, playerID, playSound]);

  // KUSA-FROM-KITTY — fires when bidding ends, the local seat is the
  // declarer, AND the Kusa was face-up in the nest during bidding.
  // We capture the "was Kusa in kitty" state during bidding via a ref
  // because G.blindBidding is reset to false at bidding.onEnd, so the
  // useEffect that fires on phase transition wouldn't otherwise see it.
  const kusaWasInKittyRef = useRef(false);
  useEffect(() => {
    if (ctx.phase === 'bidding' && G.blindBidding) {
      kusaWasInKittyRef.current = true;
    } else if (ctx.phase === 'play' || ctx.phase === 'complete') {
      // Reset for the next hand after we've moved past discard.
      kusaWasInKittyRef.current = false;
    }
  });
  const prevPhaseForKusaRef = useRef(ctx.phase);
  useEffect(() => {
    const prev = prevPhaseForKusaRef.current;
    prevPhaseForKusaRef.current = ctx.phase;
    if (
      prev === 'bidding' &&
      ctx.phase === 'discardAndTrump' &&
      kusaWasInKittyRef.current &&
      playerID === G.declarer
    ) {
      playSound('kusa');
    }
  }, [ctx.phase, G.declarer, playerID, playSound]);
  const togglePick = (key) =>
    setPicked((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  const handCounters = {
    0: tallyCards(G.tricksWon && G.tricksWon[0]),
    1: tallyCards(G.tricksWon && G.tricksWon[1]),
  };
  const lastHand =
    G.handHistory && G.handHistory.length > 0
      ? G.handHistory[G.handHistory.length - 1]
      : null;
  const moonTeam =
    lastHand && lastHand.hungTheMoon != null ? lastHand.hungTheMoon : null;
  const showMoonBanner =
    moonTeam !== null && ctx.phase === 'bidding' && G.trickCount === 0;
  const showResultBanner =
    !!lastHand && ctx.phase === 'bidding' && G.trickCount === 0;
  const canHold = !!(
    ctx.phase === 'bidding' &&
    myTurn &&
    G.highBid &&
    G.highBid.player !== playerID &&
    teamOf(G.highBid.player) === teamOf(playerID) &&
    !G.passes.includes(playerID) &&
    ['0', '1', '2', '3'].some(
      (p) => !G.passes.includes(p) && teamOf(p) !== teamOf(playerID)
    )
  );
  const legalKeys =
    ctx.phase === 'play' && myTurn ? legalPlayKeys(G, playerID) : null;
  const header = (
    <h3
      style={{
        margin: '0 0 8px',
        opacity: hasPassed ? 0.4 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {playerLabel(playerID)} ({teamLabel(teamOf(playerID))})
    </h3>
  );
  // Big "YOUR TURN" banner when it's the local seat's turn. Suppressed in
  // the 'complete' phase so the YOUR TURN pulse doesn't fight with the
  // game-over / play-again UI for the dealer.
  const turnBanner =
    myTurn && ctx.phase !== 'complete' ? (
      <div
        style={{
          margin: '4px 0 8px',
          padding: '8px 10px',
          background: '#0a8a3a',
          color: '#fff',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 700,
          textAlign: 'center',
          letterSpacing: 1,
          boxShadow: '0 0 0 0 rgba(10, 138, 58, 0.6)',
          animation: 'kusa-turn-pulse 1.6s infinite',
        }}
      >
        YOUR TURN
      </div>
    ) : null;
  const moonBanner = showMoonBanner ? (
    <div
      className="kusa-banner-in"
      style={{
        margin: '4px 0 8px',
        padding: 8,
        background: '#4a3a10',
        color: '#f5e0a0',
        border: '2px solid #d4a017',
        borderRadius: 4,
        fontSize: 14,
        fontWeight: 700,
        textAlign: 'center',
      }}
    >
      {teamLabel(moonTeam)} hung the moon!!
    </div>
  ) : null;
  // Result banner — recap the previous hand: who declared, made vs set,
  // and the per-team point swings.
  let resultBanner = null;
  if (showResultBanner) {
    const dt = lastHand.declarerTeam;
    const declarerNames =
      `${playerLabel(dt)} and ${playerLabel(dt + 2)}`;
    const verb = lastHand.made ? 'made the bid' : 'were set';
    const fmt = (n) => (n >= 0 ? `+${n}` : `${n}`);
    const t0Delta = lastHand.delta[0] ?? 0;
    const t1Delta = lastHand.delta[1] ?? 0;
    resultBanner = (
      <div
        className="kusa-banner-in"
        style={{
          margin: '4px 0 8px',
          padding: 8,
          background: lastHand.made ? '#1d3a22' : '#3a1d20',
          border: lastHand.made
            ? '1px solid #3d6e48'
            : '1px solid #6e3d40',
          borderRadius: 4,
          fontSize: 13,
          textAlign: 'center',
          color: '#e1e3ea',
        }}
      >
        <div style={{ fontWeight: 700 }}>
          {declarerNames} {verb}
        </div>
        <div style={{ marginTop: 2, fontSize: 12, color: '#bcc0c8' }}>
          {teamLabel(0)}:{' '}
          <b style={{ color: t0Delta >= 0 ? '#7dd17d' : '#e88080' }}>
            {fmt(t0Delta)}
          </b>
          {' · '}
          {teamLabel(1)}:{' '}
          <b style={{ color: t1Delta >= 0 ? '#7dd17d' : '#e88080' }}>
            {fmt(t1Delta)}
          </b>
        </div>
      </div>
    );
  }
  const blindBanner =
    G.blindBidding && ctx.phase === 'bidding' ? (
      <div
        style={{
          margin: '4px 0 8px',
          padding: 6,
          background: '#222',
          color: '#fff',
          border: '2px solid #d8c478',
          borderRadius: 4,
          fontSize: 13,
          fontWeight: 700,
          textAlign: 'center',
          letterSpacing: 0.4,
        }}
      >
        KUSA face-up — bid blind
      </div>
    ) : null;
  const errorBanner = errorMsg ? (
    <div
      className="kusa-banner-in"
      style={{
        margin: '4px 0 8px',
        padding: 6,
        background: '#3a1d20',
        border: '1px solid #c0494c',
        borderRadius: 4,
        color: '#f0b0b0',
        fontSize: 12,
        textAlign: 'center',
        fontWeight: 600,
      }}
    >
      {errorMsg}
    </div>
  ) : null;
  // Choose which trick to display: prefer the lingering (just-resolved) one.
  const displayedTrick = lingerTrick || G.currentTrick;
  const isLingering = !!lingerTrick;
  // Whoever is currently winning the trick by playing a trump card
  // out-of-suit gets their seat frame highlighted. The helper handles
  // the "trump-was-led / no-trump declared / nothing played yet"
  // edge cases by returning null.
  const trumpingSeatNow = trumpingPlayer(displayedTrick, G.trump);
  const trumpingSeatLast = trumpingPlayer(G.lastTrick || [], G.trump);
  // Show the trick area for the entire play phase, even when no cards
  // have been played yet (start of a new trick) or all cards have just
  // cleared after a linger. The four empty seat slots stay on screen so
  // the table doesn't contract between tricks. The `winningSeat` and
  // green linger background are still only applied when a trick has
  // actually resolved.
  const trickArea =
    ctx.phase === 'play' ? (
      <div
        style={{
          marginBottom: 6,
          background: isLingering ? 'rgba(125, 209, 125, 0.12)' : 'transparent',
          border: isLingering
            ? '1px solid rgba(125, 209, 125, 0.4)'
            : '1px solid transparent',
          borderRadius: 4,
          padding: isLingering ? 8 : 4,
          transition: 'background 0.3s, border-color 0.3s',
        }}
      >
        {isLingering && G.lastTrickWinner != null ? (
          // Big, obvious winner callout that replaces the "Trick:" label
          // during the linger phase. Lets players see at a glance who
          // collected the trick before the next lead is played.
          <div
            style={{
              textAlign: 'center',
              fontSize: 16,
              fontWeight: 700,
              color: '#a8e8a8',
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            {playerLabel(G.lastTrickWinner)} wins the trick
          </div>
        ) : (
          <b>Trick:</b>
        )}
        <PositionalTrick
          trick={displayedTrick || []}
          viewerSeat={playerID}
          trumpingSeat={trumpingSeatNow}
          // Only highlight the winning seat during the linger or sweep
          // phase — mid-trick (cards still landing) there's no settled
          // winner.
          winningSeat={
            (isLingering || isSweeping) && G.lastTrickWinner != null
              ? String(G.lastTrickWinner)
              : null
          }
          // Active-turn cyan glow: only during the play phase, only on
          // the live (non-lingering, non-sweeping) trick area. During
          // linger/sweep, the winner has the green trick-won glow which
          // takes precedence.
          activeSeat={
            ctx.phase === 'play' &&
            !isLingering &&
            !isSweeping &&
            ctx.currentPlayer != null
              ? String(ctx.currentPlayer)
              : null
          }
          isSweeping={isSweeping}
          playFlight={playFlight}
          onFlightComplete={() => setPlayFlight(null)}
        />
      </div>
    ) : null;
  const lastTrickArea =
    G.lastTrick && G.lastTrick.length > 0 && !isLingering ? (
      <details style={{ marginBottom: 6 }}>
        <summary style={{ cursor: 'pointer' }}>
          <b>Last trick</b>
          {G.lastTrickWinner != null &&
            ` (won by ${playerLabel(G.lastTrickWinner)})`}
        </summary>
        <PositionalTrick
          trick={G.lastTrick}
          viewerSeat={playerID}
          trumpingSeat={trumpingSeatLast}
          winningSeat={
            G.lastTrickWinner != null ? String(G.lastTrickWinner) : null
          }
        />
      </details>
    ) : null;
  const status = (
    <div style={{ marginBottom: 6 }}>
      <div>
        <b>Turn:</b> {playerLabel(ctx.currentPlayer)}
        {myTurn && <span style={{ color: '#0a0' }}> ← you</span>}
      </div>
      {ctx.phase === 'play' && (
        <div>
          <b>Trick {Math.min(G.trickCount + 1, 10)}/10</b>
          {G.leader != null && ` · leader ${playerLabel(G.leader)}`}
        </div>
      )}
    </div>
  );
  const kittyArea =
    ctx.phase === 'bidding' && G.nest && G.nest.length > 0 ? (
      <div style={{ marginBottom: 6 }}>
        <b>Kitty:</b>{' '}
        {G.nest.map((card, i) => (
          <Card key={i} card={card} disabled />
        ))}
      </div>
    ) : null;
  const historyArea =
    G.handHistory && G.handHistory.length > 0 ? (
      <details style={{ marginBottom: 6 }}>
        <summary style={{ cursor: 'pointer' }}>
          <b>Hand history</b> ({G.handHistory.length})
        </summary>
        <table
          style={{
            marginTop: 4,
            fontSize: 11,
            borderCollapse: 'collapse',
            width: '100%',
          }}
        >
          <thead>
            <tr style={{ background: '#2a2f3a' }}>
              <th style={{ padding: '2px 4px', textAlign: 'left' }}>#</th>
              <th style={{ padding: '2px 4px', textAlign: 'left' }}>
                Declaring team
              </th>
              <th style={{ padding: '2px 4px', textAlign: 'right' }}>Bid</th>
              <th style={{ padding: '2px 4px', textAlign: 'right' }}>
                Result
              </th>
              <th style={{ padding: '2px 4px', textAlign: 'right' }}>
                {teamLabel(0)}
              </th>
              <th style={{ padding: '2px 4px', textAlign: 'right' }}>
                {teamLabel(1)}
              </th>
            </tr>
          </thead>
          <tbody>
            {G.handHistory.map((h) => {
              const declarerCounters = h.counters[h.declarerTeam];
              return (
                <tr key={h.handNumber} style={{ borderTop: '1px solid #444b59' }}>
                  <td style={{ padding: '2px 4px' }}>{h.handNumber}</td>
                  <td style={{ padding: '2px 4px' }}>
                    {teamLabel(h.declarerTeam)}
                  </td>
                  <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                    {h.contractDisplay ?? h.contract}
                  </td>
                  <td
                    style={{
                      padding: '2px 4px',
                      textAlign: 'right',
                      fontWeight: 700,
                      color: h.made ? '#7dd17d' : '#e88080',
                    }}
                  >
                    {declarerCounters}
                  </td>
                  <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                    {h.scoreAfter[0]}
                  </td>
                  <td style={{ padding: '2px 4px', textAlign: 'right' }}>
                    {h.scoreAfter[1]}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </details>
    ) : null;
  const onCardClick = (card, event) => {
    if (!myTurn) return;
    const key = cardKeyOf(card);
    if (ctx.phase === 'play') {
      if (legalKeys && !legalKeys.has(key)) {
        setErrorMsg(whyIllegal(G, playerID, card) || "Can't play that card");
        return;
      }
      // Capture the hand-card's on-screen rect for the FLIP animation.
      // PositionalTrick will measure the destination slot in a
      // useLayoutEffect and animate from this rect down to (0,0).
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        setPlayFlight({
          fromRect: {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
          },
          cardKey: key,
          // Unique stamp so re-clicks of the same card key (rare but
          // possible if you immediately play a card with the same
          // identity on the next trick) re-trigger the animation.
          stamp: Date.now() + Math.random(),
        });
      }
      moves.playCard(key);
    } else if (
      ctx.phase === 'discardAndTrump' &&
      G.buried === null &&
      playerID === G.declarer
    ) {
      togglePick(key);
    }
  };
  const handIsHidden = hand.some((c) => c.hidden);
  const arranged = arrangeHand(hand, G.trump, arrangement, trumpSide);
  // "Hand version" = how many hands have been completed so far. Used as
  // a key prefix so when a new hand is dealt, every Card's key changes,
  // forcing React to mount fresh DOM nodes — which lets the staggered
  // deal animation replay. Changing arrangement preference doesn't
  // change this version, so re-sorting your hand doesn't replay the deal.
  const handVersion = (G.handHistory && G.handHistory.length) || 0;
  // Global card index for stagger timing (so the deal feels like one
  // continuous wave rather than four parallel columns dealing at once).
  // We bump this counter inside renderCard.
  let _dealIndex = 0;
  const renderCard = (c) => {
    const key = cardKeyOf(c);
    const illegal = legalKeys && !legalKeys.has(key);
    const dealIdx = _dealIndex++;
    return (
      <Card
        key={`${handVersion}-${key}`}
        card={c}
        onClick={(e) => onCardClick(c, e)}
        selected={
          ctx.phase === 'discardAndTrump' && picked.includes(key)
        }
        disabled={!myTurn}
        illegal={illegal}
        className="kusa-card-deal"
        style={{ animationDelay: `${dealIdx * 80}ms` }}
      />
    );
  };
  const showTrumpSidePicker =
    !handIsHidden &&
    isColorArrangement(arrangement) &&
    G.trump &&
    G.trump !== 'no-trump';
  // Hand-arrangement preferences moved to the sidebar (handPrefsPanel
  // below). Header row is now just the label so the hand layout reads
  // cleanly on mobile too.
  //
  // minHeight here keeps the table size constant from deal to end-of-
  // hand: even as cards are played and the hand shrinks, the empty
  // space below stays reserved. clamp scales the floor with viewport
  // — narrow phones need more vertical room because the hand wraps
  // into more rows there.
  const handArea = (
    <div
      style={{
        marginBottom: 6,
        minHeight: 'clamp(260px, 30vw, 320px)',
      }}
    >
      <div style={{ marginBottom: 4 }}>
        <b>Your hand ({hand.length}):</b>
      </div>
      {handIsHidden ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
          {hand.map((c, i) => (
            <Card key={i} card={c} disabled />
          ))}
        </div>
      ) : arranged.flat ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 4, justifyContent: 'center' }}>
          {arranged.flat.map(renderCard)}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginTop: 4,
            flexWrap: 'wrap',
            // Center the color-group columns within the row so the hand
            // sits in the middle of the table instead of pinned to the
            // left edge. Cards within a single color stay contiguous.
            justifyContent: 'center',
          }}
        >
          {arranged.columns.map((col) => (
            <div
              key={col.color}
              style={{
                display: 'flex',
                flexDirection: 'row',
                // Allow a color-group that's wider than the container
                // to wrap to a second row of its own rather than
                // pushing the page wider than the viewport. Rare hand
                // (8+ cards in one color), but the fix is cheap.
                flexWrap: 'wrap',
                maxWidth: '100%',
              }}
            >
              {col.cards.map(renderCard)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  const legalBids = validBidAmounts(G.highBid && G.highBid.amount);
  const effectiveBid = legalBids.includes(bidAmount)
    ? bidAmount
    : legalBids[0];
  const isAskedPartner =
    ctx.phase === 'discardAndTrump' &&
    G.partnerCalledTrump &&
    playerID === partnerOf(G.declarer);
  const declarerControls =
    ctx.phase === 'discardAndTrump' && myTurn && playerID === G.declarer;
  const controls = (
    <div>
      {ctx.phase === 'bidding' && myTurn && (
        <div style={controlsRowStyle}>
          {legalBids.length > 0 ? (
            <>
              <select
                value={effectiveBid}
                onChange={(e) => setBidAmount(Number(e.target.value))}
                style={actionSelectStyle}
              >
                {legalBids.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <button onClick={() => moves.bid(effectiveBid)} style={actionBtnStyle}>
                Bid
              </button>
            </>
          ) : (
            <span style={{ color: '#8a909a' }}>
              No legal bids
            </span>
          )}
          <button onClick={() => moves.pass()} style={actionBtnStyle}>
            Pass
          </button>
          {canHold && (
            <button onClick={() => moves.hold()} style={actionBtnStyle}>
              Hold
            </button>
          )}
        </div>
      )}
      {declarerControls && (
        <div>
          {G.buried === null && (
            <div>
              <div style={controlsRowStyle}>
                <button
                  disabled={picked.length !== 5}
                  onClick={() => {
                    moves.discard(picked);
                    setPicked([]);
                  }}
                  style={{
                    ...actionBtnStyle,
                    opacity: picked.length !== 5 ? 0.5 : 1,
                    cursor: picked.length !== 5 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Discard selected ({picked.length}/5)
                </button>
              </div>
              <div style={{ marginTop: 6, color: '#aab0b8', fontSize: 12 }}>
                Pick 5 cards to bury — they go to the team that wins the
                last trick.
              </div>
            </div>
          )}
          {G.buried !== null && G.trump === null && (
            <div style={{ ...controlsRowStyle, marginTop: 8 }}>
              <select
                value={trumpChoice}
                onChange={(e) => setTrumpChoice(e.target.value)}
                style={actionSelectStyle}
              >
                <option value="red">Red</option>
                <option value="yellow">Yellow</option>
                <option value="green">Green</option>
                <option value="blue">Blue</option>
                <option value="no-trump">No Trump</option>
              </select>
              <button
                onClick={() => moves.declareTrump(trumpChoice)}
                style={actionBtnStyle}
              >
                Declare Trump
              </button>
            </div>
          )}
          {G.trump === null && !G.partnerCalledTrump && (
            <div style={{ ...controlsRowStyle, marginTop: 8 }}>
              <button
                onClick={() => moves.askPartnerToCallTrump()}
                style={actionBtnStyle}
              >
                Ask {playerLabel(partnerOf(playerID))} to call trumps (+5)
              </button>
            </div>
          )}
        </div>
      )}
      {ctx.phase === 'discardAndTrump' && myTurn && isAskedPartner && (
        <div>
          <div style={controlsRowStyle}>
            <select
              value={trumpChoice}
              onChange={(e) => setTrumpChoice(e.target.value)}
              style={actionSelectStyle}
            >
              <option value="red">red</option>
              <option value="yellow">yellow</option>
              <option value="green">green</option>
              <option value="blue">blue</option>
              <option value="no-trump">no-trump</option>
            </select>
            <button
              onClick={() => moves.declareTrump(trumpChoice)}
              style={actionBtnStyle}
            >
              Declare Trump
            </button>
          </div>
          <div style={{ marginTop: 6, color: '#666', fontSize: 12 }}>
            {playerLabel(G.declarer)} asked you to call trumps. Pick a
            color (or No Trump) and you'll lead the first trick.
          </div>
        </div>
      )}
    </div>
  );
  // Sidebar: hand-arrangement settings panel. Only shown when the
  // local seat actually has a visible hand (not during blind bidding).
  // Shared style for sidebar prefs selects (sound, arrangement, trump
  // column) — keeps them visually consistent.
  const prefsSelectStyle = {
    width: '100%',
    fontSize: 13,
    padding: '6px 8px',
    border: '1px solid rgba(184, 154, 106, 0.5)',
    borderRadius: 4,
    background: '#fff',
    color: '#3a2614',
    boxSizing: 'border-box',
  };
  const prefsLabelStyle = {
    display: 'block',
    fontSize: 12,
    color: '#6e3a1e',
    marginBottom: 3,
    fontWeight: 500,
  };
  // Settings panel always renders so sound preference is always
  // adjustable. The arrangement + trump-column sections are gated on
  // hand visibility since they only matter when you can see your hand.
  const handPrefsPanel = (
    <div className="kusa-sidebar-section kusa-panel">
      <div className="kusa-panel-label">Settings</div>
      <label style={prefsLabelStyle}>Sound</label>
      <select
        value={soundMode}
        onChange={(e) => {
          setSoundMode(e.target.value);
          setStoredPref(PREF_SOUND_MODE_KEY, e.target.value);
        }}
        style={prefsSelectStyle}
        title="When to play sound effects"
      >
        {SOUND_MODE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {!handIsHidden && (
        <>
          <label style={{ ...prefsLabelStyle, marginTop: 10 }}>
            Arrangement
          </label>
          <select
            value={arrangement}
            onChange={(e) => {
              setArrangement(e.target.value);
              setStoredPref(PREF_ARRANGEMENT_KEY, e.target.value);
            }}
            style={prefsSelectStyle}
            title="How to sort your hand"
          >
            {ARRANGEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {showTrumpSidePicker && (
            <>
              <label style={{ ...prefsLabelStyle, marginTop: 10 }}>
                Trump column
              </label>
              <select
                value={trumpSide}
                onChange={(e) => {
                  setTrumpSide(e.target.value);
                  setStoredPref(PREF_TRUMP_SIDE_KEY, e.target.value);
                }}
                style={prefsSelectStyle}
                title="Where to put the trump column"
              >
                {TRUMP_SIDE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <div
      className="kusa-game-layout"
      style={{ fontSize: 13, fontFamily: 'system-ui, sans-serif' }}
    >
      <div className="kusa-main kusa-tabletop-rim">
       <div className="kusa-tabletop-surface">
        {trumpRipple && (
          <div
            key={trumpRipple.key}
            className="kusa-trump-ripple"
            style={{
              '--ripple-color':
                TRUMP_RIPPLE_COLOR[trumpRipple.color] ||
                'rgba(255, 255, 255, 0.4)',
            }}
          />
        )}
        {header}
        <StatusBubbles G={G} ctx={ctx} handCounters={handCounters} />
        <BidSequence G={G} ctx={ctx} />
        {turnBanner}
        {moonBanner}
        {resultBanner}
        {blindBanner}
        {errorBanner}
        {status}
        {kittyArea}
        {trickArea}
        {lastTrickArea}
        {handArea}
        {controls}
        {historyArea}
      {(() => {
        // Show the gameover banner when either (a) we're in the
        // 'complete' phase (the normal path), OR (b) a team has hit
        // the target score (the safety net — fires even if the phase
        // transition fails for any reason, so the user always sees a
        // proper game-end UI).
        const target = G.targetScore || 300;
        const gameComplete =
          ctx.phase === 'complete' ||
          G.score[0] >= target ||
          G.score[1] >= target;
        if (!gameComplete) return null;
        const winner =
          G.score[0] === G.score[1]
            ? null
            : G.score[0] > G.score[1]
            ? 0
            : 1;
        // Play again is only callable in the 'complete' phase (the move
        // doesn't exist in other phases). Even on the safety-net path
        // where score is at target but phase hasn't transitioned, we
        // still gate the button on myTurn so the right seat owns it.
        const canPlayAgain = myTurn && ctx.phase === 'complete';
        return (
          <div className="kusa-gameover-banner">
            <div className="kusa-gameover-eyebrow">Game over</div>
            <div className="kusa-gameover-winner">
              {winner != null
                ? `${teamLabel(winner)} wins!`
                : 'Tied — play another hand.'}
            </div>
            <div className="kusa-gameover-score">
              Final score &middot; {teamLabel(0)} <b style={{ color: '#fff' }}>{G.score[0]}</b>
              {'  '}·{'  '}
              {teamLabel(1)} <b style={{ color: '#fff' }}>{G.score[1]}</b>
            </div>
            <div className="kusa-gameover-actions">
              {canPlayAgain ? (
                <button
                  onClick={() => moves.playAgain()}
                  style={{
                    ...primaryBtnStyle,
                    fontSize: 16,
                    padding: '12px 24px',
                    minHeight: 48,
                  }}
                >
                  Play again (same seats)
                </button>
              ) : (
                <div style={{ fontSize: 13, color: '#c8b076', fontStyle: 'italic' }}>
                  Waiting for {playerLabel(ctx.currentPlayer)} to start the
                  next game (or hit Leave).
                </div>
              )}
            </div>
          </div>
        );
      })()}
       </div>
      </div>
      <div className="kusa-sidebar">
        {handPrefsPanel}
        <Chat matchID={matchID} mySeat={playerID} />
        <RulesPanel />
      </div>
    </div>
  );
}
// ============================================================
// CLIENT VARIANTS
// ============================================================
const KusaClientLocal = Client({
  game: Kusa,
  numPlayers: NUM_PLAYERS,
  board: KusaBoard,
  multiplayer: Local(),
});
const KusaClientNet = Client({
  game: Kusa,
  numPlayers: NUM_PLAYERS,
  board: KusaBoard,
  multiplayer: SocketIO({ server: SERVER_URL }),
});
// ============================================================
// LOBBY UTILITIES
// ============================================================
function useMatchInfo(matchID, intervalMs = 2500) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!matchID) return;
    let live = true;
    const fetch = async () => {
      try {
        const m = await lobbyClient.getMatch(GAME_NAME, matchID);
        if (live) {
          setInfo(m);
          setError(null);
        }
      } catch (e) {
        if (live) setError(e.message || String(e));
      }
    };
    fetch();
    const id = setInterval(fetch, intervalMs);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [matchID, intervalMs]);
  return { info, error };
}
// ============================================================
// SCREEN: LANDING
// ============================================================
function Landing({ onGoCreate, onGoJoin, onGoOpenGames }) {
  return (
    <PageShell title="Kusa">
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        {/* Hero card — the Kusa face card as a brand mark above the form.
            Sized small enough not to crowd the buttons, with a tiny rotation
            for the "fanned in hand" feel. The drop-shadow is the only place
            in the lobby that uses one; landing is the cinematic moment. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 22,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/cards/kusa.svg`}
            alt="Kusa"
            style={{
              width: 180,
              height: 'auto',
              transform: 'rotate(-3deg)',
              filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.35))',
              borderRadius: 8,
            }}
          />
        </div>
        <div className="kusa-form-panel">
          <p style={{ margin: 0 }}>
            A four-player partnership card game. Bring three friends and
            share the code, or browse Open Games to find a public table
            with room for you.
          </p>
          <div style={{ marginTop: 18 }}>
            {/* Create is the primary CTA — full-width emphasis. Join and
                View open games are equal-priority secondary actions and
                share a row below. */}
            <button
              onClick={onGoCreate}
              style={{ ...primaryBtnStyle, width: '100%' }}
            >
              Create new game
            </button>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button
                onClick={onGoJoin}
                style={{ ...secondaryBtnStyle, flex: 1 }}
              >
                Join with code
              </button>
              <button
                onClick={onGoOpenGames}
                style={{ ...secondaryBtnStyle, flex: 1 }}
              >
                View open games
              </button>
            </div>
          </div>
        </div>
        <RulesPanel />
      </div>
    </PageShell>
  );
}
// ============================================================
// SCREEN: CREATE
// ============================================================
function CreateMatch({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState(300);
  // Visibility: 'private' (share the code) or 'public' (listed in Open
  // Games for anyone to browse and join). Default private since most
  // games are with known friends.
  const [visibility, setVisibility] = useState('private');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr('Please enter your name.');
      return;
    }
    setBusy(true);
    setErr(null);
    // Soft-cap on public games: if 15+ public matches already exist,
    // refuse to create a new public one. Friendly error rather than
    // silent failure. Race condition is small for friend-only use.
    if (visibility === 'public') {
      try {
        const res = await fetch(`${SERVER_URL}/scores`);
        if (res.ok) {
          const data = await res.json();
          const publicCount = (data.matches || []).length;
          if (publicCount >= 15) {
            setErr(
              'The public lobby is full at the moment (15/15 games). ' +
                'Try Private for now, or come back in a bit.'
            );
            setBusy(false);
            return;
          }
        }
      } catch {
        // If the count check fails (network blip), allow the create
        // to proceed — better to occasionally exceed the cap than
        // block legitimate users on a transient error.
      }
    }
    try {
      const created = await lobbyClient.createMatch(GAME_NAME, {
        numPlayers: NUM_PLAYERS,
        setupData: { targetScore: target },
        // boardgame.io's lobby API hides unlisted matches from
        // listMatches calls. We only flag the match as listed when
        // the host explicitly chose Public.
        unlisted: visibility !== 'public',
      });
      const matchID = created.matchID;
      const joined = await lobbyClient.joinMatch(GAME_NAME, matchID, {
        playerID: '0',
        playerName: name.trim(),
      });
      onCreated({
        matchID,
        playerID: '0',
        credentials: joined.playerCredentials,
        name: name.trim(),
        // Timestamp of when this player joined the match. Used by the
        // chat to render a "Before you joined" divider for messages
        // older than this — so the catch-up history is visually
        // distinct from messages arriving after you sat down.
        joinedAt: Date.now(),
      });
    } catch (e2) {
      setErr(e2.message || String(e2));
      setBusy(false);
    }
  };
  // Inline button-style visibility chooser. Persimmon when selected,
  // cream when not — same palette as the rest of the lobby.
  const visBtnBase = {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #b89a6a',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
  };
  const visBtnSelected = {
    ...visBtnBase,
    background: '#6e3a1e',
    color: '#f5dca8',
    borderColor: '#6e3a1e',
  };
  const visBtnUnselected = {
    ...visBtnBase,
    background: '#f7f1e3',
    color: '#6e3a1e',
  };
  return (
    <PageShell title="Create a new game" onBack={onCancel}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        <form onSubmit={submit} className="kusa-form-panel">
          <Field label="Your name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              autoFocus
              placeholder="e.g. Holli"
              style={inputStyle}
            />
          </Field>
          <Field label="Game length (points to win)">
            <select
              value={target}
              onChange={(e) => setTarget(Number(e.target.value))}
              style={inputStyle}
            >
              {TARGET_SCORE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Visibility">
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                style={
                  visibility === 'private' ? visBtnSelected : visBtnUnselected
                }
              >
                Private
              </button>
              <button
                type="button"
                onClick={() => setVisibility('public')}
                style={
                  visibility === 'public' ? visBtnSelected : visBtnUnselected
                }
              >
                Public
              </button>
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#6e3a1e',
                opacity: 0.85,
                lineHeight: 1.45,
              }}
            >
              {visibility === 'private'
                ? 'Players will need your match code or share link to join. The game won’t appear in Open Games.'
                : 'Anyone can find this game in Open Games and click to join.'}
            </div>
          </Field>
          {err && <ErrorBox>{err}</ErrorBox>}
          <button
            type="submit"
            disabled={busy}
            style={{ ...primaryBtnStyle, marginTop: 6 }}
          >
            {busy ? 'Creating…' : 'Create game'}
          </button>
        </form>
      </div>
    </PageShell>
  );
}
// ============================================================
// SCREEN: JOIN BY CODE
// ============================================================
function JoinMatchByCode({ onSubmit, onCancel, initialCode = '' }) {
  const [code, setCode] = useState(initialCode);
  const submit = (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length === 0) return;
    onSubmit(c);
  };
  return (
    <PageShell title="Join a game" onBack={onCancel}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        <form onSubmit={submit} className="kusa-form-panel">
          <Field label="Match code (from the host)">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              placeholder="e.g. H4MTQ"
              style={{
                ...inputStyle,
                textTransform: 'uppercase',
                letterSpacing: 3,
                fontSize: 18,
                fontWeight: 600,
                textAlign: 'center',
              }}
            />
          </Field>
          <button
            type="submit"
            style={{ ...primaryBtnStyle, marginTop: 6 }}
          >
            Continue
          </button>
        </form>
      </div>
    </PageShell>
  );
}
// ============================================================
// SCREEN: OPEN GAMES (browse public matches with open seats)
// ============================================================
function OpenGames({ onPick, onCancel, onCreate }) {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  // Poll for the public match list every 5 seconds while this screen
  // is open so new games appear without the user needing to refresh.
  // We hit our custom /scores endpoint (not the boardgame.io
  // listMatches API) because we need live scores + phase info for
  // the "in progress" section, which lobby metadata doesn't include.
  useEffect(() => {
    let live = true;
    const fetchMatches = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/scores`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!live) return;
        setMatches(data.matches || []);
        setError(null);
      } catch (e) {
        if (!live) return;
        setError(e.message || String(e));
      }
    };
    fetchMatches();
    const id = setInterval(fetchMatches, 5000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, []);
  // Split into joinable (at least one open seat) and in-progress (all
  // seats taken, game is happening). Sort by most-recently-updated so
  // active games surface first.
  const joinable = (matches || [])
    .filter((m) => (m.players || []).some((s) => !s.name))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  const inProgress = (matches || [])
    .filter((m) => {
      const seats = m.players || [];
      return seats.length === 4 && seats.every((s) => s.name);
    })
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  return (
    <PageShell title="Open games" onBack={onCancel}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {error && <ErrorBox>{error}</ErrorBox>}
        {matches === null && !error && (
          <div className="kusa-form-panel" style={{ textAlign: 'center' }}>
            <div style={{ color: '#6e3a1e', fontStyle: 'italic' }}>
              Loading open games…
            </div>
          </div>
        )}
        {matches !== null &&
          joinable.length === 0 &&
          inProgress.length === 0 &&
          !error && (
            <div className="kusa-form-panel" style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 15,
                  color: '#4a2614',
                  marginBottom: 8,
                  fontWeight: 600,
                }}
              >
                No open games right now.
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#6e3a1e',
                  marginBottom: 14,
                  opacity: 0.85,
                }}
              >
                Be the first — start a public game and others can find it
                here.
              </div>
              <button onClick={onCreate} style={primaryBtnStyle}>
                Create new game
              </button>
            </div>
          )}
        {joinable.length > 0 && (
          <>
            <SectionLabel>Joinable</SectionLabel>
            <MatchList
              matches={joinable}
              variant="joinable"
              onPick={onPick}
            />
          </>
        )}
        {inProgress.length > 0 && (
          <div style={{ marginTop: joinable.length > 0 ? 22 : 0 }}>
            <SectionLabel>In progress</SectionLabel>
            <MatchList matches={inProgress} variant="in-progress" />
          </div>
        )}
      </div>
    </PageShell>
  );
}

// Small uppercase header used between the Joinable and In-progress
// sections of OpenGames.
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1.8,
        color: '#6e3a1e',
        textTransform: 'uppercase',
        margin: '0 0 10px 4px',
      }}
    >
      {children}
    </div>
  );
}

// Renders a list of match panels. variant === 'joinable' makes each
// panel clickable (calls onPick). variant === 'in-progress' shows a
// score display instead of seat slots and renders as a non-clickable
// info panel.
function MatchList({ matches, variant, onPick }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {matches.map((m) =>
        variant === 'joinable' ? (
          <JoinableMatchPanel key={m.matchID} m={m} onPick={onPick} />
        ) : (
          <InProgressMatchPanel key={m.matchID} m={m} />
        )
      )}
    </div>
  );
}

function JoinableMatchPanel({ m, onPick }) {
  const seats = m.players || [];
  const filled = seats.filter((s) => s.name).length;
  const target = m.targetScore || 300;
  return (
    <button
      type="button"
      onClick={() => onPick(m.matchID)}
      className="kusa-form-panel"
      style={{
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        padding: '14px 16px',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 10,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            color: '#6e3a1e',
            textTransform: 'uppercase',
          }}
        >
          Match{' '}
          <code
            style={{
              fontSize: 14,
              letterSpacing: 1.2,
              color: '#4a2614',
            }}
          >
            {m.matchID}
          </code>
        </div>
        <div style={{ fontSize: 12, color: '#6e3a1e', opacity: 0.75 }}>
          {filled}/4 seated · play to {target}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: 6,
        }}
      >
        {seats.map((s) => {
          const team = teamOf(s.id);
          const warm = team === 0;
          const taken = !!s.name;
          return (
            <div
              key={s.id}
              style={{
                padding: '6px 8px',
                borderRadius: 6,
                background: warm ? '#efd6b9' : '#dde4ee',
                border:
                  '1px solid ' + (warm ? '#a8682e' : '#5e7a96'),
                opacity: taken ? 1 : 0.65,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: warm ? '#8a5028' : '#3a5a78',
                  marginBottom: 2,
                }}
              >
                SEAT {Number(s.id) + 1}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#3a2614',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {taken ? (
                  s.name
                ) : (
                  <em style={{ color: warm ? '#a8682e' : '#5e7a96' }}>
                    open
                  </em>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: '#6e3a1e',
          fontWeight: 600,
        }}
      >
        Click to join →
      </div>
    </button>
  );
}

function InProgressMatchPanel({ m }) {
  const seats = m.players || [];
  // Build team-label strings from the seat names. Team 0 = seats 0+2,
  // Team 1 = seats 1+3. Defensive against missing names so a partial
  // state doesn't crash the panel.
  const nameOf = (i) => {
    const s = seats.find((x) => Number(x.id) === i);
    return (s && s.name) || `Player ${i + 1}`;
  };
  const team0Label = `${nameOf(0)} & ${nameOf(2)}`;
  const team1Label = `${nameOf(1)} & ${nameOf(3)}`;
  const score0 = (m.score && m.score[0]) || 0;
  const score1 = (m.score && m.score[1]) || 0;
  const target = m.targetScore || 300;
  const phaseLabel =
    m.phase === 'complete'
      ? 'finished'
      : m.phase === 'play'
      ? 'playing'
      : m.phase === 'bidding'
      ? 'bidding'
      : m.phase === 'discardAndTrump'
      ? 'choosing trump'
      : 'in progress';
  return (
    <div
      className="kusa-form-panel"
      style={{
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            color: '#6e3a1e',
            textTransform: 'uppercase',
          }}
        >
          Match{' '}
          <code
            style={{
              fontSize: 14,
              letterSpacing: 1.2,
              color: '#4a2614',
            }}
          >
            {m.matchID}
          </code>
        </div>
        <div style={{ fontSize: 12, color: '#6e3a1e', opacity: 0.75 }}>
          Hand {m.handNumber || 1} · {phaseLabel} · play to {target}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <ScoreRow
          warm
          label={team0Label}
          score={score0}
          target={target}
        />
        <ScoreRow
          warm={false}
          label={team1Label}
          score={score1}
          target={target}
        />
      </div>
    </div>
  );
}

function ScoreRow({ warm, label, score, target }) {
  const pct = Math.max(0, Math.min(1, target > 0 ? score / target : 0));
  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: 6,
        background: warm ? '#efd6b9' : '#dde4ee',
        border: '1px solid ' + (warm ? '#a8682e' : '#5e7a96'),
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: warm ? '#4a2510' : '#1f3a5e',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginBottom: 4,
        }}
        title={label}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: warm ? '#4a2510' : '#1f3a5e',
          marginBottom: 4,
        }}
      >
        {score}
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: warm ? '#8a5028' : '#3a5a78',
            marginLeft: 4,
          }}
        >
          / {target}
        </span>
      </div>
      {/* Progress bar */}
      <div
        style={{
          height: 4,
          background: warm
            ? 'rgba(74, 37, 16, 0.18)'
            : 'rgba(31, 58, 94, 0.18)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct * 100}%`,
            height: '100%',
            background: warm ? '#a8682e' : '#5e7a96',
            transition: 'width 0.3s ease-out',
          }}
        />
      </div>
    </div>
  );
}
// ============================================================
// SCREEN: SEAT PICKER
// ============================================================
function SeatPicker({ matchID, onJoined, onCancel }) {
  const { info, error } = useMatchInfo(matchID);
  const [name, setName] = useState('');
  const [picking, setPicking] = useState(null);
  const [err, setErr] = useState(null);
  const join = async (playerID) => {
    if (!name.trim()) {
      setErr('Enter your name first.');
      return;
    }
    setErr(null);
    setPicking(playerID);
    try {
      const joined = await lobbyClient.joinMatch(GAME_NAME, matchID, {
        playerID,
        playerName: name.trim(),
      });
      onJoined({
        matchID,
        playerID,
        credentials: joined.playerCredentials,
        name: name.trim(),
        joinedAt: Date.now(),
      });
    } catch (e) {
      setErr(e.message || String(e));
      setPicking(null);
    }
  };
  if (error) {
    const isMissing = error.includes('not found') || /404/.test(error);
    return (
      <PageShell
        title={isMissing ? 'Game not found' : "Couldn't load match"}
        onBack={onCancel}
      >
        {isMissing ? (
          <ErrorBox>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Sorry — no active game with code <code>{matchID}</code>.
            </div>
            <div style={{ fontWeight: 400 }}>
              Double-check the code with whoever set up the game (it's
              case-sensitive, all uppercase, no zeros or letter O). Or ask
              them to send you the join link directly.
            </div>
          </ErrorBox>
        ) : (
          <ErrorBox>{error}</ErrorBox>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={onCancel} style={primaryBtnStyle}>
            Try a different code
          </button>
        </div>
      </PageShell>
    );
  }
  if (!info) {
    return (
      <PageShell title="Loading…">
        <div style={{ color: '#888' }}>Looking up match {matchID}…</div>
      </PageShell>
    );
  }
  const seats = info.players || [];
  return (
    <PageShell title={`Join match ${matchID}`} onBack={onCancel}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div className="kusa-form-panel">
          <Field label="Your name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              autoFocus
              placeholder="e.g. Holli"
              style={inputStyle}
            />
          </Field>
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: '#6e3a1e',
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Pick a seat
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {seats.map((s) => {
              const taken = !!s.name;
              const team = teamOf(s.id);
              const warm = team === 0;
              return (
                <button
                  key={s.id}
                  disabled={taken || picking !== null}
                  onClick={() => join(String(s.id))}
                  style={{
                    ...seatBtnStyle,
                    opacity: taken ? 0.55 : 1,
                    cursor: taken ? 'not-allowed' : 'pointer',
                    // Warm persimmon tint for team 0 seats, cool indigo
                    // tint for team 1 — mirrors the team-bubble palette.
                    background: warm ? '#efd6b9' : '#dde4ee',
                    border: '1px solid ' + (warm ? '#a8682e' : '#5e7a96'),
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: warm ? '#4a2510' : '#1f3a5e',
                    }}
                  >
                    Seat {Number(s.id) + 1}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: warm ? '#8a5028' : '#3a5a78',
                    }}
                  >
                    Team {team + 1}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#3a2614' }}>
                    {taken ? s.name : (
                      <em style={{ color: warm ? '#a8682e' : '#5e7a96' }}>
                        open
                      </em>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {err && <ErrorBox>{err}</ErrorBox>}
        </div>
      </div>
    </PageShell>
  );
}
// ============================================================
// SCREEN: PLAY
// ============================================================
function PlayScreen({ matchID, playerID, credentials, name, onLeave }) {
  // Names are now provided by KusaBoard via PlayerNamesContext, driven by
  // matchData (live via WebSocket). No useMatchInfo polling needed here.

  // Validate the match still exists on the server. If the droplet was
  // restarted (in-memory match state wiped) or the match was deleted,
  // the saved session in localStorage points at nothing — show the user
  // an error rather than hanging on a dead WebSocket forever. We also
  // wipe localStorage so a tab-close-and-reopen doesn't loop them back
  // into the same dead match; parent state keeps the session reference
  // until they click "Back to lobby" (which calls onLeave).
  const [validating, setValidating] = useState(true);
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await lobbyClient.getMatch(GAME_NAME, matchID);
        if (!cancelled) setValidating(false);
      } catch (e) {
        if (cancelled) return;
        const msg = (e && e.message) || '';
        // Treat "not found" as a stale-session signal; any other error
        // (network, etc.) we let through so the Client can retry on its
        // own — only a confirmed 404 wipes the saved session.
        if (/not\s*found/i.test(msg) || /404/.test(msg)) {
          setInvalid(true);
          clearSession();
        } else {
          setValidating(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matchID]);

  if (invalid) {
    return (
      <PageShell title="Match no longer available">
        <ErrorBox>
          The match <b>{matchID}</b> is no longer available on the server
          (it may have ended or the server was restarted). Returning you
          to the lobby…
        </ErrorBox>
        <button onClick={onLeave} style={primaryBtnStyle}>
          Back to lobby
        </button>
      </PageShell>
    );
  }
  if (validating) {
    return (
      <PageShell title={`Match ${matchID}`}>
        <div style={{ color: '#888', fontSize: 13 }}>Reconnecting…</div>
      </PageShell>
    );
  }

  return (
    <PageShell title={`Match ${matchID}`} onLeave={onLeave}>
      {/* Centered column. Was left-aligned within the wider PageShell;
          margin: '0 auto' here centers the whole game UI on desktop. */}
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>
          You are <b>{name}</b>, seat {Number(playerID) + 1}.
        </div>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: 10,
            background: '#fafafa',
          }}
        >
          <KusaClientNet
            playerID={playerID}
            matchID={matchID}
            credentials={credentials}
          />
        </div>
      </div>
    </PageShell>
  );
}
// ============================================================
// QUICK-SEAT MODE
// ============================================================
function QuickSeatScreen() {
  const matchID = MATCH_PARAM || 'kusa-default';
  const playerID = SEAT_PARAM;
  return (
    <PageShell title="Kusa (dev quick-seat)">
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>
          Match: <b>{matchID}</b> · Seat: <b>{Number(playerID) + 1}</b>
        </div>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: 10,
            background: '#fafafa',
          }}
        >
          <KusaClientNet
            playerID={playerID}
            matchID={matchID}
            {...(TARGET_PARAM
              ? { setupData: { targetScore: TARGET_PARAM } }
              : {})}
          />
        </div>
      </div>
    </PageShell>
  );
}
// ============================================================
// 4-PANEL DEV VIEW
// ============================================================
function DevView() {
  const [gameSession, setGameSession] = useState(0);
  const [targetScore, setTargetScore] = useState(300);
  const startNewGame = () => {
    if (gameSession === 0) {
      setGameSession(1);
      return;
    }
    if (window.confirm('Start a new game? Current scores will be reset.')) {
      setGameSession((s) => s + 1);
    }
  };
  return (
    <PageShell title="Kusa — dev view (offline 4-panel)">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          margin: '8px 0 16px',
          padding: 8,
          background: '#f0f0f0',
          borderRadius: 6,
        }}
      >
        <label style={{ fontSize: 13 }}>
          Game length:{' '}
          <select
            value={targetScore}
            onChange={(e) => setTargetScore(Number(e.target.value))}
          >
            {TARGET_SCORE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={startNewGame}
          style={{ fontSize: 13, padding: '4px 10px' }}
        >
          {gameSession === 0 ? 'Start game' : 'New game'}
        </button>
      </div>
      {gameSession === 0 ? (
        <div style={{ padding: 16, color: '#666' }}>
          Pick length, click Start.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: 12,
          }}
        >
          {['0', '1', '2', '3'].map((id) => (
            <div
              key={`${gameSession}-${id}`}
              style={{
                border: '1px solid #ccc',
                padding: 10,
                borderRadius: 8,
                background: '#fafafa',
              }}
            >
              <KusaClientLocal
                key={`${gameSession}-${id}`}
                matchID={`kusa-${gameSession}`}
                playerID={id}
                setupData={{ targetScore }}
              />
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
// ============================================================
// SHARED UI BITS + GLOBAL STYLES
// ============================================================
// Lobby-screen styles — wabi-sabi palette. Persimmon-and-cream rather
// than the default React-blue, and bamboo-tinted input borders so the
// lobby flow feels like the same product as the in-game table.
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid rgba(184, 154, 106, 0.5)',
  borderRadius: 4,
  background: '#fff',
  color: '#3a2614',
  boxSizing: 'border-box',
};
const primaryBtnStyle = {
  fontSize: 14,
  fontWeight: 600,
  padding: '10px 18px',
  background: '#6e3a1e',
  color: '#f5dca8',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  letterSpacing: 0.3,
};
const secondaryBtnStyle = {
  fontSize: 14,
  padding: '10px 18px',
  background: '#f7f1e3',
  color: '#6e3a1e',
  border: '1px solid #b89a6a',
  borderRadius: 4,
  cursor: 'pointer',
};
const seatBtnStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  border: '1px solid rgba(184, 154, 106, 0.5)',
  borderRadius: 6,
  background: '#f7f1e3',
  cursor: 'pointer',
  fontSize: 13,
};
// In-game action buttons (Bid / Pass / Hold / Declare Trump / Discard /
// Ask partner). Sized for a comfortable mobile touch target (44px tall,
// 15px text) — the previous browser-default buttons were ~24px tall and
// hard to hit on a phone. Selects next to these buttons should use
// actionSelectStyle so the row visually matches.
const actionBtnStyle = {
  fontSize: 15,
  fontWeight: 600,
  padding: '10px 16px',
  minHeight: 44,
  background: '#fff',
  color: '#2c7be5',
  border: '1.5px solid #2c7be5',
  borderRadius: 6,
  cursor: 'pointer',
  // Allow long button labels (e.g. "Ask Bartholomew to call trumps (+5)")
  // to wrap rather than push the row out past the viewport.
  whiteSpace: 'normal',
};
const actionSelectStyle = {
  fontSize: 15,
  padding: '10px 12px',
  minHeight: 44,
  border: '1.5px solid #2c7be5',
  borderRadius: 6,
  background: '#fff',
  color: '#222',
};
// Flex row for control groups — gap so big buttons don't crowd each
// other, and wrap so on narrow viewports they stack instead of overflow.
const controlsRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
};
// Global stylesheet for animations (injected once into the head).
function GlobalStyles() {
  useEffect(() => {
    const id = 'kusa-global-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
@keyframes kusa-turn-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(10, 138, 58, 0.55); }
  60%  { box-shadow: 0 0 0 10px rgba(10, 138, 58, 0); }
  100% { box-shadow: 0 0 0 0 rgba(10, 138, 58, 0); }
}
`;
    document.head.appendChild(el);
  }, []);
  return null;
}
// Collapsible rules summary. Used on the Landing screen, the in-match
// waiting room, and the in-game board so players have a quick refresher
// available anywhere in the app.
// Rules content as data so the panel can render each section as its
// own nested <details> for click-to-expand-just-this-section behavior.
const KUSA_RULES = [
  {
    heading: 'Players & teams',
    paragraphs: [
      <>4 players in 2 fixed partnerships. Partners sit opposite. First
      team to the target score (set when creating the game) wins.</>,
    ],
  },
  {
    heading: 'Deck',
    paragraphs: [
      <>45 cards: ranks <code>1, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14</code>{' '}
      in four colors (red, yellow, green, blue) plus 1 special{' '}
      <b>Kusa</b> card. Within a color, rank order high to low is{' '}
      <code>1, 14, 13, …, 5</code> — the <code>1</code> plays as the
      highest.</>,
    ],
  },
  {
    heading: 'Counters',
    paragraphs: [
      <>Cards captured during play are worth points:{' '}
      <code>1</code>=15, <code>14</code>=10, <code>10</code>=10,{' '}
      <code>5</code>=5, <b>Kusa</b>=20. 180 total per hand.</>,
    ],
  },
  {
    heading: 'Deal',
    paragraphs: [
      <>10 cards to each player, 5 to the "nest" in the middle (last
      face-up, others face-down). Deal rotates clockwise.</>,
    ],
  },
  {
    heading: 'Bidding',
    paragraphs: [
      <>Starts left of dealer, clockwise. Bids are{' '}
      <code>70, 75, 80, …, 180</code> in steps of 5. Each bid must beat
      the current high.</>,
      <>On your turn: <b>bid</b>, <b>pass</b> (locks you out), or{' '}
      <b>hold</b> — preserves your right to bid later, but only when
      your partner has the high bid and at least one opponent is
      still active.</>,
      <>A bid of 180 ends bidding immediately. If all four players pass,
      the first passer is stuck with a contract of 70.</>,
      <><b>If Kusa is the face-up nest card</b>, all players bid blind
      (without seeing their hands).</>,
    ],
  },
  {
    heading: 'Contract',
    paragraphs: [
      <>Your bid is your contract — the minimum counters the declaring
      team must capture this hand to score (instead of losing the bid
      amount).</>,
    ],
  },
  {
    heading: "Declarer's choice",
    paragraphs: [
      <>The winning bidder takes the nest into hand (now holding 15
      cards), then discards 5 face-down (those "buried" cards go to
      whichever team wins the <i>last</i> trick), and names trump (a
      color or No Trump).</>,
      <><b>Or</b> asks their partner to call trump instead, which
      raises the contract by 5 (capped at 180) and gives the partner
      the lead.</>,
    ],
  },
  {
    heading: 'Play',
    paragraphs: [
      <>10 tricks. Trump-caller leads the first. Follow the lead color
      if able.</>,
      <><b>Kusa is always playable</b> — it never has to follow suit.
      Kusa is the highest trump in colored contracts; in No Trump it
      stands alone and can't be forced out.</>,
      <>If trump is led (including Kusa-led), other players must play
      trump if they have any.</>,
      <>Trick winner = highest trump played, or highest lead-color
      card if no trump played. Winner leads next.</>,
    ],
  },
  {
    heading: 'Scoring',
    paragraphs: [
      <>Declaring team captures at least the contract → they score the
      actual counters captured. Fall short → they lose the contract
      amount (negative).</>,
      <>Opposing team always scores what they captured. Buried
      discards count for whichever team wins the last trick.</>,
      <><b>Hung the moon</b>: one team captures all 180 counters in a
      hand.</>,
    ],
  },
  {
    heading: 'Game end',
    paragraphs: [
      <>First team to the target wins. If both teams cross the target
      in the same hand, higher score wins; ties play another hand.</>,
    ],
  },
];

function RulesPanel({ defaultOpen = false }) {
  return (
    <details
      open={defaultOpen}
      className="kusa-sidebar-section kusa-panel kusa-panel-details"
    >
      <summary>How to play</summary>
      <div style={{ marginTop: 10 }}>
        {KUSA_RULES.map((section) => (
          <details key={section.heading} className="kusa-rule-section">
            <summary>{section.heading}</summary>
            {section.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </details>
        ))}
      </div>
    </details>
  );
}
function PageShell({ title, onBack, onLeave, children }) {
  return (
    <div
      style={{
        padding: 16,
        fontFamily: 'system-ui, sans-serif',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        // Was 900; widened to 1400 so desktop has room for the larger
        // cards, the bigger central trick area, AND the chat/prefs
        // sidebar that kicks in above 900px viewport.
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <h1 className="kusa-pageshell-title">{title}</h1>
        {onBack && (
          <button onClick={onBack} style={secondaryBtnStyle}>
            Back
          </button>
        )}
        {onLeave && (
          <button onClick={onLeave} style={secondaryBtnStyle}>
            Leave
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'block', margin: '10px 0', fontSize: 13 }}>
      <div style={{ marginBottom: 4, color: '#555' }}>{label}</div>
      {children}
    </label>
  );
}
function ErrorBox({ children }) {
  return (
    <div
      style={{
        margin: '8px 0',
        padding: 8,
        background: '#ffe4e4',
        border: '1px solid #c33',
        borderRadius: 4,
        color: '#900',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
// ============================================================
// MAIN APP / ROUTER
// ============================================================
export default function App() {
  return (
    <>
      <GlobalStyles />
      <AppRouter />
    </>
  );
}
function AppRouter() {
  if (MODE_DEV_LOCAL) return <DevView />;
  if (MODE_QUICKSEAT) return <QuickSeatScreen />;
  return <LobbyApp />;
}
function LobbyApp() {
  const [session, setSession] = useState(() => {
    const stored = loadSession();
    if (stored && MATCH_PARAM && stored.matchID !== MATCH_PARAM) {
      clearSession();
      return null;
    }
    return stored;
  });
  const [screen, setScreen] = useState(
    session ? 'in-match' : MATCH_PARAM ? 'seat-picker' : 'landing'
  );
  const [pendingMatchID, setPendingMatchID] = useState(MATCH_PARAM || null);
  useEffect(() => {
    if (session) saveSession(session);
    else clearSession();
  }, [session]);
  const onLeave = useCallback(() => {
    clearSession();
    setSession(null);
    setScreen('landing');
    if (window.history && window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  if (screen === 'landing') {
    return (
      <Landing
        onGoCreate={() => setScreen('create')}
        onGoJoin={() => setScreen('enter-code')}
        onGoOpenGames={() => setScreen('open-games')}
      />
    );
  }
  if (screen === 'create') {
    return (
      <CreateMatch
        onCancel={() => setScreen('landing')}
        onCreated={(s) => {
          setSession(s);
          setScreen('in-match');
        }}
      />
    );
  }
  if (screen === 'enter-code') {
    return (
      <JoinMatchByCode
        onCancel={() => setScreen('landing')}
        onSubmit={(code) => {
          setPendingMatchID(code);
          setScreen('seat-picker');
        }}
      />
    );
  }
  if (screen === 'open-games') {
    return (
      <OpenGames
        onCancel={() => setScreen('landing')}
        onCreate={() => setScreen('create')}
        onPick={(matchID) => {
          setPendingMatchID(matchID);
          setScreen('seat-picker');
        }}
      />
    );
  }
  if (screen === 'seat-picker') {
    return (
      <SeatPicker
        matchID={pendingMatchID}
        onCancel={() => setScreen('landing')}
        onJoined={(s) => {
          setSession(s);
          setScreen('in-match');
        }}
      />
    );
  }
  if (screen === 'in-match' && session) {
    // Always render PlayScreen (which renders the boardgame.io Client).
    // KusaBoard internally shows the waiting-room UI until all four seats
    // are filled, so chat works during the wait.
    return (
      <PlayScreen
        matchID={session.matchID}
        playerID={session.playerID}
        credentials={session.credentials}
        name={session.name}
        onLeave={onLeave}
      />
    );
  }
  return (
    <Landing
      onGoCreate={() => setScreen('create')}
      onGoJoin={() => setScreen('enter-code')}
    />
  );
}
